import { useState, useEffect, useMemo } from 'react';
import { Badge, fmt, Spinner, SectionLabel, SlipPreviewModal } from './UI';
import { exportToExcel, exportSubmissionsToExcel, exportHubSummaryToExcel, downloadUsersTemplate, parseUsersExcel } from '../lib/parser';
import { getAllSubmissions, getArchivedSubmissions, getAllTransactions, getAllUploads, deleteUpload, deleteUploadData, deleteSubmission, flagSubmissionForReview, clearSubmissionReview, subscribeToSubmissions, getAllHubUsers, createHubUser, updateHubUser, deleteHubUser, hashPassword, addSubmission, addDirectSubmission } from '../lib/supabase';
import PaymentSheet from './PaymentSheet';
import BillingSection from './BillingSection';

export default function AdminDashboard({ hubData, uploadMeta, onNewUpload, onRefresh, section: sectionProp, onSectionChange, fullAdmin = true, hubUser, onLogout }) {
  const [submissions, setSubmissions] = useState([]);
  const [archivedSubmissions, setArchivedSubmissions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('All');
  const [sortBy, setSortBy] = useState('pendency');
  const [view, setView] = useState('summary');
  const [section, setSection] = useState(sectionProp ?? 'payment');
  const [recoHub, setRecoHub] = useState('All');
  const [recoSearch, setRecoSearch] = useState('');
  const [recoPage, setRecoPage] = useState(1);
  const RECO_PAGE_SIZE = 200;
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [confirmDeleteId,     setConfirmDeleteId]     = useState(null);
  const [deleting,            setDeleting]            = useState(false);
  const [confirmDeleteDataId, setConfirmDeleteDataId] = useState(null);
  const [deletingData,        setDeletingData]        = useState(false);
  const [confirmDeleteSubId,  setConfirmDeleteSubId]  = useState(null);
  const [deletingSub,         setDeletingSub]         = useState(false);
  // Send-back-for-review state
  const [flagSubId,   setFlagSubId]   = useState(null);   // which submission's note input is open
  const [flagNote,    setFlagNote]    = useState('');
  const [flagging,    setFlagging]    = useState(false);
  // Hub Users tab state
  const [hubUsers,          setHubUsers]          = useState([]);
  const [usersLoading,      setUsersLoading]      = useState(false);
  const [newUsername,       setNewUsername]       = useState('');
  const [newPassword,       setNewPassword]       = useState('');
  const [newHubName,        setNewHubName]        = useState('');
  const [newUserRole,       setNewUserRole]       = useState('hub');      // 'hub' | 'manager'
  const [newManagedHubs,    setNewManagedHubs]    = useState(new Set()); // for manager role
  const [creatingUser,      setCreatingUser]      = useState(false);
  const [resetUserId,       setResetUserId]       = useState(null);  // which user's reset form is open
  const [resetPassword,     setResetPassword]     = useState('');
  const [resettingPw,       setResettingPw]       = useState(false);
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState(null);
  const [deletingUser,      setDeletingUser]      = useState(false);
  // Inline edit role / hub assignment
  const [editUserId,      setEditUserId]      = useState(null);  // which row is in edit mode
  const [editRole,        setEditRole]        = useState('hub');
  const [editHubName,     setEditHubName]     = useState('');
  const [editManagedHubs, setEditManagedHubs] = useState(new Set());
  const [savingEdit,      setSavingEdit]      = useState(false);
  // Bulk import state
  const [importResults,   setImportResults]   = useState(null); // { created, skipped, errors }
  const [importing,       setImporting]       = useState(false);
  // Submission history filter states
  const [summaryMonth,  setSummaryMonth]  = useState('All');
  const [expandedHub,   setExpandedHub]   = useState(null); // hub name or null
  // Admin submit on behalf of hub
  const [adminSubHub,     setAdminSubHub]     = useState(null); // null = modal closed
  const [adminSubTxnId,   setAdminSubTxnId]   = useState('');   // DB id or '' = direct
  const [adminSubType,    setAdminSubType]    = useState('cms');
  const [adminSubAmount,  setAdminSubAmount]  = useState('');
  const [adminSubDate,    setAdminSubDate]    = useState('');
  const [adminSubRef,     setAdminSubRef]     = useState('');
  const [adminSubRemarks, setAdminSubRemarks] = useState('');
  const [adminSubLoading, setAdminSubLoading] = useState(false);
  const [histHub,       setHistHub]       = useState('All');
  const [histMonth,     setHistMonth]     = useState('All');
  const [histDateFrom,  setHistDateFrom]  = useState('');
  const [histDateTo,    setHistDateTo]    = useState('');
  const [histKind,      setHistKind]      = useState('All'); // All / Full / Short / Zero
  const [histOpenOnly,  setHistOpenOnly]  = useState(false); // only show subs for open (non-closed) dates
  const [viewRemarks,   setViewRemarks]   = useState(null); // full remarks text to show in modal
  const [previewSlip,   setPreviewSlip]   = useState(null); // slip URL to show in inline preview

  // Cell selection for Excel-style sum bar — Map<cellKey, numericValue>
  // cellKey = any unique string; value = the number shown in that cell
  const [selectedCells, setSelectedCells] = useState(new Map());

  function toggleCell(key, value) {
    setSelectedCells(prev => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key); else next.set(key, Number(value) || 0);
      return next;
    });
  }

  useEffect(() => {
    // Always load submissions — hub team data persists even without an upload record
    fetchSubmissions();

    // Real-time listener (works with or without uploadMeta)
    const channel = subscribeToSubmissions(uploadMeta?.id, () => fetchSubmissions());
    return () => channel.unsubscribe();
  }, [uploadMeta]); // eslint-disable-line

  async function fetchSubmissions() {
    setLoading(true);
    try {
      const [active, archived] = await Promise.all([
        getAllSubmissions(),
        getArchivedSubmissions(),
      ]);
      setSubmissions(active);
      setArchivedSubmissions(archived);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function fetchTransactions() {
    setTxnLoading(true);
    try {
      // First try: transactions for the current upload
      let data = await getAllTransactions(uploadMeta?.id);
      // Check if the result actually matches the hub names we're showing.
      // If the latest upload mis-parsed all hubs as "Unknown", the transactions
      // won't match v_latest_pendency hub names — so fall back with known hubs.
      const knownHubs = hubData.map(h => h.hub).filter(h => h && h !== 'Unknown');
      const hubSet    = new Set(knownHubs);
      const hasMatch  = data.length > 0 && data.some(t => hubSet.has(t.hub));
      if (!hasMatch) {
        // Pass knownHubs so getAllTransactions finds the correct upload_id
        data = await getAllTransactions(null, knownHubs);
      }
      setTransactions(data);
    } catch (e) {
      console.error(e);
    }
    setTxnLoading(false);
  }

  // Reset cached transactions whenever a new file is uploaded so the
  // reconciliation tab re-fetches with the correct upload_id next time it opens.
  useEffect(() => {
    setTransactions([]);
  }, [uploadMeta?.id]); // eslint-disable-line

  // Lazy-load reconciliation data when the tab is first opened.
  // Also fetch transactions for Summary tab (needed for month-wise pendency filter).
  useEffect(() => {
    if ((view === 'reconciliation' || view === 'summary') && transactions.length === 0) {
      fetchTransactions();
    }
    if (view === 'uploads' && uploads.length === 0) {
      fetchUploads();
    }
    if (section === 'users' && hubUsers.length === 0) {
      fetchHubUsers();
    }
  }, [view, section, transactions.length]); // eslint-disable-line

  async function fetchHubUsers() {
    setUsersLoading(true);
    try { setHubUsers(await getAllHubUsers()); }
    catch (e) { console.error(e); }
    setUsersLoading(false);
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword) return;
    if (newUserRole === 'hub' && !newHubName) return;
    if (newUserRole === 'manager' && newManagedHubs.size === 0) { alert('Select at least one hub for the manager.'); return; }
    setCreatingUser(true);
    try {
      const hash = await hashPassword(newPassword);
      const user = await createHubUser(
        newUsername, hash,
        newUserRole === 'hub' ? newHubName : '',
        newUserRole,
        newUserRole === 'manager' ? [...newManagedHubs] : [],
      );
      setHubUsers(prev => [user, ...prev]);
      setNewUsername(''); setNewPassword(''); setNewHubName('');
      setNewUserRole('hub'); setNewManagedHubs(new Set());
    } catch (err) {
      alert('Create failed: ' + (err.message?.includes('duplicate') ? 'Username already exists.' : err.message));
    }
    setCreatingUser(false);
  }

  async function handleResetPassword(userId) {
    if (!resetPassword) return;
    setResettingPw(true);
    try {
      const hash = await hashPassword(resetPassword);
      await updateHubUser(userId, { passwordHash: hash });
      setResetUserId(null); setResetPassword('');
    } catch (err) { alert('Reset failed: ' + err.message); }
    setResettingPw(false);
  }

  async function handleToggleUserActive(user) {
    try {
      await updateHubUser(user.id, { isActive: !user.is_active });
      setHubUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
    } catch (err) { alert('Update failed: ' + err.message); }
  }

  function startEditUser(u) {
    setEditUserId(u.id);
    setEditRole(u.role || 'hub');
    setEditHubName(u.hub_name || '');
    setEditManagedHubs(new Set(u.managed_hubs || []));
  }

  async function handleSaveUserEdit(userId) {
    if (editRole === 'hub' && !editHubName) { alert('Please select a hub.'); return; }
    if (editRole === 'manager' && editManagedHubs.size === 0) { alert('Select at least one hub for the manager.'); return; }
    setSavingEdit(true);
    try {
      const updates = {
        role:        editRole,
        hubName:     editRole === 'hub'     ? editHubName          : '',
        managedHubs: editRole === 'manager' ? [...editManagedHubs] : null,
      };
      await updateHubUser(userId, updates);
      setHubUsers(prev => prev.map(u => u.id === userId
        ? { ...u, role: editRole, hub_name: editRole === 'hub' ? editHubName : '', managed_hubs: editRole === 'manager' ? [...editManagedHubs] : [] }
        : u
      ));
      setEditUserId(null);
    } catch (e) { alert('Update failed: ' + e.message); }
    setSavingEdit(false);
  }

  async function handleDeleteUser(userId) {
    setDeletingUser(true);
    try {
      await deleteHubUser(userId);
      setHubUsers(prev => prev.filter(u => u.id !== userId));
      setConfirmDeleteUserId(null);
    } catch (err) { alert('Delete failed: ' + err.message); }
    setDeletingUser(false);
  }

  async function handleBulkImport(e) {
    const file = e.target.files[0];
    e.target.value = '';                         // reset so same file can be re-selected
    if (!file) return;
    setImporting(true);
    setImportResults(null);
    try {
      const buffer = await file.arrayBuffer();
      const { users, errors: parseErrors } = parseUsersExcel(buffer);

      let created = 0;
      const skipped = [];
      const rowErrors = [...parseErrors];

      for (const u of users) {
        try {
          const hash = await hashPassword(u.password);
          const newUser = await createHubUser(u.username, hash, u.hub_name);
          setHubUsers(prev => [newUser, ...prev]);
          created++;
        } catch (err) {
          const reason = err.message?.toLowerCase().includes('duplicate')
            ? 'username already exists'
            : err.message;
          skipped.push(`${u.username}: ${reason}`);
        }
      }

      setImportResults({ created, skipped, errors: rowErrors });
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
    setImporting(false);
  }

  async function fetchUploads() {
    setUploadsLoading(true);
    try {
      const data = await getAllUploads();
      setUploads(data);
    } catch (e) {
      console.error(e);
    }
    setUploadsLoading(false);
  }

  async function handleDeleteUpload(upload) {
    setDeleting(true);
    try {
      // First wipe associated transactions + hub_data so Hub Summary clears too.
      // Submissions are preserved (SET NULL FK on transaction_id).
      await deleteUploadData(upload.id);
      // Then remove the upload record and the stored Excel file.
      await deleteUpload(upload.id, upload.filename, upload.file_url);
      const remaining = uploads.filter(u => u.id !== upload.id);
      setUploads(remaining);
      setConfirmDeleteId(null);
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
    setDeleting(false);
  }

  // Wipe all hub_data + transactions + submissions imported from this upload.
  // The upload record itself is kept. Refreshes the dashboard when done.
  async function handleDeleteData(upload) {
    setDeletingData(true);
    try {
      await deleteUploadData(upload.id);
      setConfirmDeleteDataId(null);
      onRefresh();
    } catch (e) {
      alert('Delete data failed: ' + e.message);
    }
    setDeletingData(false);
  }

  async function handleDeleteSubmission(subId) {
    setDeletingSub(true);
    try {
      await deleteSubmission(subId, 'admin_archive');
      setSubmissions(prev => prev.filter(s => s.id !== subId));
      setConfirmDeleteSubId(null);
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
    setDeletingSub(false);
  }

  async function handleFlagSubmission(subId) {
    setFlagging(true);
    try {
      await flagSubmissionForReview(subId, flagNote.trim() || null);
      setSubmissions(prev => prev.map(s => s.id === subId
        ? { ...s, review_status: 'flagged', review_note: flagNote.trim() || null }
        : s
      ));
      setFlagSubId(null);
      setFlagNote('');
    } catch (e) { alert('Failed to send back: ' + e.message); }
    setFlagging(false);
  }

  async function handleClearFlag(subId) {
    try {
      await clearSubmissionReview(subId);
      setSubmissions(prev => prev.map(s => s.id === subId
        ? { ...s, review_status: null, review_note: null }
        : s
      ));
    } catch (e) { alert('Failed to clear flag: ' + e.message); }
  }

  // Admin submit on behalf of a hub
  function openAdminSubmit(hubName) {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    setAdminSubHub(hubName);
    setAdminSubTxnId('');
    setAdminSubType('cms');
    setAdminSubAmount('');
    setAdminSubDate(today);
    setAdminSubRef('');
    setAdminSubRemarks('');
  }

  async function handleAdminSubmit(e) {
    e.preventDefault();
    if (!adminSubAmount || !adminSubDate) return;
    setAdminSubLoading(true);
    try {
      const entry = {
        type: adminSubType, amount: parseFloat(adminSubAmount),
        ref: adminSubRef, date: adminSubDate,
        remarks: adminSubRemarks ? `[ADMIN] ${adminSubRemarks}` : '[ADMIN]',
        submittedBy: 'Admin',
      };
      if (adminSubTxnId) {
        await addSubmission(uploadMeta?.id, adminSubHub, adminSubTxnId, entry);
      } else {
        await addDirectSubmission(uploadMeta?.id, adminSubHub, adminSubDate, entry);
      }
      await fetchSubmissions();
      setAdminSubHub(null);
    } catch (err) {
      alert('Submit failed: ' + err.message);
    }
    setAdminSubLoading(false);
  }

  // Build per-hub submission map
  const subMap = useMemo(() => {
    const m = {};
    submissions.forEach(s => { if (!m[s.hub]) m[s.hub] = []; m[s.hub].push(s); });
    return m;
  }, [submissions]);

  // Map "hub|collection_date" → submissions — used for hub-level summary totals
  // where collapsing all same-date subs together is correct.
  // Falls back to the loaded transactions list to recover the date when the Supabase
  // join fails (e.g. submission has a stale transaction_id from a previous upload cycle).
  const subByHubDate = useMemo(() => {
    const m = {};
    // Build a quick ID→transaction lookup for date-recovery fallback
    const txnById = {};
    transactions.forEach(t => { txnById[t.id] = t; });
    submissions.forEach(s => {
      if (s.is_direct) return;
      const fallbackTxn = txnById[s.transaction_id];
      // Prefer the date from the Supabase join; fall back to the loaded transactions list
      const effDate = s.txn_date || fallbackTxn?.collection_date;
      if (!effDate) return; // date truly unknowable — skip
      // Also recover txn_amount_to_collect if the join failed (needed for split-date fallback matching)
      const enriched = {
        ...s,
        txn_amount_to_collect: s.txn_amount_to_collect ?? fallbackTxn?.amount_to_collect ?? null,
      };
      const key = `${enriched.hub}|${effDate}`;
      if (!m[key]) m[key] = [];
      m[key].push(enriched);
    });
    return m;
  }, [submissions, transactions]);

  // Map "hub|collection_date" → direct (prior-deposit) submissions.
  // These are submitted BEFORE the Excel row exists (today_direct transactions).
  // Kept separate so the expanded row can display and count them correctly.
  const subByHubDateDirect = useMemo(() => {
    const m = {};
    submissions.forEach(s => {
      if (!s.is_direct || !s.txn_date) return;
      const key = `${s.hub}|${s.txn_date}`;
      if (!m[key]) m[key] = [];
      m[key].push(s);
    });
    return m;
  }, [submissions]);

  // Map "hub|collection_date" → real (non-today_direct) transaction.
  // Used in submission history to resolve Total Collection for is_direct submissions
  // once their date is covered by the current Excel upload.
  const txnByHubDate = useMemo(() => {
    const m = {};
    transactions.forEach(t => {
      if (t.status === 'today_direct') return;
      const key = `${t.hub}|${t.collection_date}`;
      // Prefer the first real transaction found for this date
      if (!m[key]) m[key] = t;
    });
    return m;
  }, [transactions]);

  // Map transaction DB id → submissions — used for the expanded date-wise breakdown
  // so that split transactions on the same date (e.g. two rows for 9-Mar) each show
  // only their own submissions rather than the combined total for the date.
  // Also enriches each submission with a resolved txn_amount_to_collect if the original
  // Supabase join failed (stale transaction_id), using the loaded transactions list.
  const subByTxnId = useMemo(() => {
    const m = {};
    const txnById = {};
    transactions.forEach(t => { txnById[t.id] = t; });
    submissions.forEach(s => {
      if (!s.transaction_id || s.is_direct) return;
      // Enrich with recovered amount_to_collect if join failed
      const resolved = {
        ...s,
        txn_amount_to_collect: s.txn_amount_to_collect
          ?? txnById[s.transaction_id]?.amount_to_collect
          ?? null,
      };
      if (!m[s.transaction_id]) m[s.transaction_id] = [];
      m[s.transaction_id].push(resolved);
    });
    return m;
  }, [submissions, transactions]);

  // Map hub → submissions that are truly orphaned (txn_date null AND transaction_id not in
  // the current transactions list). These come from submissions made before the in-place
  // upload fix was added — their original transaction was deleted on re-upload.
  // Used as a last-resort fallback in the expanded row view, matched by submitted amount.
  const subHubOrphan = useMemo(() => {
    const m = {};
    const txnById = {};
    transactions.forEach(t => { txnById[t.id] = t; });
    submissions.forEach(s => {
      if (s.is_direct) return;
      const effDate = s.txn_date || txnById[s.transaction_id]?.collection_date;
      if (effDate) return; // not orphaned — already handled by subByHubDate
      if (!m[s.hub]) m[s.hub] = [];
      m[s.hub].push(s);
    });
    return m;
  }, [submissions, transactions]);

  // Available months for Hub Summary filter — derived from transactions (preferred)
  // or fall back to the comma-separated months string stored in hub_data rows.
  const summaryMonths = useMemo(() => {
    const seen = new Set();
    if (transactions.length > 0) {
      transactions.forEach(t => { if (t.month) seen.add(t.month); });
    } else {
      hubData.forEach(h => {
        if (h.months) h.months.split(',').forEach(m => { const t = m.trim(); if (t) seen.add(t); });
      });
    }
    return ['All', ...[...seen].sort().reverse()];
  }, [transactions, hubData]);

  // Enrich hub rows with live submission data.
  // When summaryMonth === 'All': use pre-aggregated hubData totals.
  // When a specific month is selected: re-aggregate from transaction rows for that
  // month so TO COLLECT / DEPOSITED / DIFF reflect only that month's data.
  const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const enriched = useMemo(() => {
    // Pre-compute which hub|date keys have ALL rows Closed so they can be excluded
    // from hub-level totals (matching the expanded-row visibility rules).
    const CLOSED_LS_E = ['CLOSED', 'Closed', 'closed'];
    const _hubDateGroups = {};
    transactions.forEach(t => {
      const k = `${t.hub}|${t.collection_date}`;
      if (!_hubDateGroups[k]) _hubDateGroups[k] = [];
      _hubDateGroups[k].push(t);
    });
    const allClosedDateKeys = new Set(
      Object.entries(_hubDateGroups)
        .filter(([, g]) => g.every(t => CLOSED_LS_E.includes(t.line_status)))
        .map(([k]) => k)
    );
    // Transactions that are visible in hub summary (all-Closed dates excluded)
    const summaryTxns = transactions.filter(
      t => !allClosedDateKeys.has(`${t.hub}|${t.collection_date}`)
    );

    // Helper: for a hub's transactions (current upload only), compute the submitted total
    // using subByHubDate keyed by the DATES present in the current upload.
    // This excludes submissions for dates outside the current upload scope (e.g. old upload
    // dates that are "not asked") while still correctly counting submissions from any
    // upload cycle whose date falls within the current upload's date range.
    function computeSubmitted(hubTxnList) {
      const hub = hubTxnList[0]?.hub;
      if (!hub) return { submitted: 0, countedIds: new Set() };

      // Unique dates covered by the current upload for this hub
      const uniqueDates = [...new Set(hubTxnList.map(t => t.collection_date))];
      const countedIds  = new Set();
      let submitted     = 0;

      // Sum all submissions that are mapped to a date in the current upload.
      // subByHubDate already handles: exact join, txnById-recovered dates, and enriched amounts.
      uniqueDates.forEach(date => {
        (subByHubDate[`${hub}|${date}`] || []).forEach(s => {
          if (!countedIds.has(s.id)) { countedIds.add(s.id); submitted += Number(s.amount || 0); }
        });
      });

      // Orphan fallback: submissions whose date is completely unrecoverable (truly stale IDs).
      // Match them by amount against a current transaction's amount_to_collect.
      (subHubOrphan[hub] || []).forEach(s => {
        if (s.is_direct) return;
        const matchesTxn = hubTxnList.some(t => t.amount_to_collect > 0 && Number(s.amount) === Number(t.amount_to_collect));
        if (matchesTxn && !countedIds.has(s.id)) { countedIds.add(s.id); submitted += Number(s.amount || 0); }
      });

      return { submitted, countedIds };
    }

    if (summaryMonth === 'All') {
      return hubData.map(h => {
        const subs       = subMap[h.hub] || [];
        const directSubs = subs.filter(s => s.is_direct);

        // Use summaryTxns (all-Closed dates excluded) so hub totals match visible rows
        const hubTxns    = summaryTxns.filter(t => t.hub === h.hub && t.status !== 'today_direct');
        const uploadDates = new Set(hubTxns.map(t => t.collection_date));

        // Direct subs whose date is NOT yet in the current Excel upload → "Future Dep."
        const futureDeposited = directSubs
          .filter(s => !s.txn_date || !uploadDates.has(s.txn_date))
          .reduce((acc, s) => acc + Number(s.amount), 0);

        // Direct subs whose date IS now covered by the current Excel upload → count as submitted
        const matchedDirectAmt = directSubs
          .filter(s => s.txn_date && uploadDates.has(s.txn_date))
          .reduce((acc, s) => acc + Number(s.amount), 0);

        const baseSubmitted = hubTxns.length > 0
          ? computeSubmitted(hubTxns).submitted
          : subs.filter(s => !s.is_direct).reduce((acc, s) => acc + Number(s.amount), 0);
        const submitted = baseSubmitted + matchedDirectAmt;

        // Recompute total_to_collect from visible transactions (not DB-stored aggregate)
        const totalToCollect = hubTxns.reduce((s, t) => s + Number(t.amount_to_collect || 0), 0);
        const pendency = Math.max(0, totalToCollect - submitted);
        const lastSub  = subs.length > 0 ? subs[subs.length - 1] : null;
        return { ...h, total_to_collect: totalToCollect, totalToCollect, submitted, futureDeposited, pendency, lastSub, subCount: computeSubmitted(hubTxns).countedIds.size || subs.filter(s => !s.is_direct).length };
      });
    }

    // Month-specific: aggregate transactions for the selected month
    const [mName, yy] = summaryMonth.split('-');
    const mIdx     = MN.indexOf(mName);           // 0-based month index
    const fullYear = 2000 + parseInt(yy, 10);

    const hubAgg = {};
    summaryTxns.filter(t => t.month === summaryMonth).forEach(t => {
      if (!hubAgg[t.hub]) {
        const meta = hubData.find(h => h.hub === t.hub) || {};
        hubAgg[t.hub] = {
          hub: t.hub, zone: t.zone || meta.zone, hub_type: t.hub_type || meta.hub_type,
          total_diff: 0, total_to_collect: 0, total_deposited: 0,
        };
      }
      hubAgg[t.hub].total_diff        += t.diff              || 0;
      hubAgg[t.hub].total_to_collect  += t.amount_to_collect || 0;
      hubAgg[t.hub].total_deposited   += t.deposited         || 0;
    });

    return Object.values(hubAgg).map(h => {
      const allSubs = subMap[h.hub] || [];
      // Direct subs for this month
      const direct = allSubs.filter(s => {
        if (!s.txn_date || !s.is_direct) return false;
        const [sy, sm] = s.txn_date.split('-').map(Number);
        return sy === fullYear && sm === mIdx + 1;
      });

      // Use summaryTxns (all-Closed dates excluded) to scope submitted correctly
      const hubMonthTxns = summaryTxns.filter(t => t.hub === h.hub && t.status !== 'today_direct' && t.month === summaryMonth);
      const uploadDates  = new Set(hubMonthTxns.map(t => t.collection_date));

      // Direct subs whose date is NOT in the current Excel upload → "Future Dep."
      const futureDeposited = direct
        .filter(s => !s.txn_date || !uploadDates.has(s.txn_date))
        .reduce((acc, s) => acc + Number(s.amount), 0);

      // Direct subs whose date IS now in the current Excel upload → count as submitted
      const matchedDirectAmt = direct
        .filter(s => s.txn_date && uploadDates.has(s.txn_date))
        .reduce((acc, s) => acc + Number(s.amount), 0);

      const { submitted: baseSubmitted, countedIds } = hubMonthTxns.length > 0
        ? computeSubmitted(hubMonthTxns)
        : { submitted: 0, countedIds: new Set() };
      const submitted = baseSubmitted + matchedDirectAmt;

      const pendency = Math.max(0, (h.total_to_collect || 0) - submitted);
      const allMonth = [...direct];
      const lastSub  = allMonth.length > 0 ? allMonth[allMonth.length - 1] : null;
      return { ...h, submitted, futureDeposited, pendency, lastSub, subCount: countedIds.size };
    });
  }, [hubData, transactions, summaryMonth, subMap, subHubOrphan]); // eslint-disable-line

  const zones = ['All', ...new Set(enriched.map(h => h.zone).filter(Boolean))];

  const filtered = enriched
    .filter(h => zoneFilter === 'All' || h.zone === zoneFilter)
    .filter(h => (h.hub || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'pendency') return b.pendency - a.pendency;
      if (sortBy === 'hub') return (a.hub || '').localeCompare(b.hub || '');
      if (sortBy === 'submitted') return b.submitted - a.submitted;
      return 0;
    });

  const totalPendency        = enriched.reduce((s, h) => s + h.pendency, 0);
  // totalSubmitted = linked submissions only (future/direct subs excluded from pendency)
  const totalSubmitted       = enriched.reduce((s, h) => s + h.submitted, 0);
  const totalFutureDeposited = enriched.reduce((s, h) => s + (h.futureDeposited || 0), 0);
  // hubData is already scoped to the current upload (fetched by upload_id in App.js).
  // Use open_* fields so CLOSED rows are excluded from the stat card totals,
  // falling back to total_* for older rows that pre-date the open_* columns.
  const totalToCollect       = hubData.reduce((s, h) => s + Number(h.open_to_collect ?? h.openToCollect ?? h.total_to_collect ?? h.totalToCollect  ?? 0), 0);
  const totalDeposited       = hubData.reduce((s, h) => s + Number(h.open_deposited  ?? h.openDeposited  ?? h.total_deposited  ?? h.totalDeposited  ?? 0), 0);
  const totalDiff            = hubData.reduce((s, h) => s + Number(h.open_diff       ?? h.openDiff       ?? h.total_diff       ?? h.totalDiff       ?? 0), 0);
  const alertHubs = enriched.filter(h => h.pendency > 0 && h.subCount === 0);

  // ── Submission history filters ────────────────────────────────────────────
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function historyDate(s) {
    return s.txn_date || s.submission_date || null;
  }
  function subMonth(s) {
    const baseDate = historyDate(s);
    if (!baseDate) return null;
    const [y, m] = baseDate.split('-');
    return `${MONTH_NAMES[parseInt(m, 10) - 1]}-${String(y).slice(2)}`;
  }
  function subKind(s) {
    // Strip optional [ADMIN] prefix (admin-submitted entries) and trim whitespace
    // before checking the kind tag so detection works regardless of entry path.
    const core = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
    if (core.startsWith('[LOSS]'))   return 'Loss';
    if (core.startsWith('[SHORT]'))  return 'Short';
    if (core.startsWith('[EXCESS]')) return 'Excess';
    return 'Full';
  }
  // Available hubs + months for dropdowns
  const histHubs   = useMemo(() => ['All', ...[...new Set(submissions.map(s => s.hub).filter(Boolean))].sort()], [submissions]);
  const histMonths = useMemo(() => {
    const seen = new Set();
    submissions.forEach(s => { const m = subMonth(s); if (m) seen.add(m); });
    return ['All', ...[...seen].sort().reverse()];
  }, [submissions]); // eslint-disable-line
  // Set of "hub|collection_date" keys that are NOT closed in the uploaded file
  const openDatesSet = useMemo(() => {
    const CLOSED = ['CLOSED', 'Closed', 'closed'];
    const s = new Set();
    transactions.forEach(t => {
      if (!CLOSED.includes(t.line_status) && t.hub && t.collection_date)
        s.add(`${t.hub}|${t.collection_date}`);
    });
    return s;
  }, [transactions]); // eslint-disable-line

  // Filtered submissions for history view
  const filteredSubs = useMemo(() => submissions.filter(s => {
    if (histHub   !== 'All' && s.hub !== histHub) return false;
    if (histMonth !== 'All' && subMonth(s) !== histMonth) return false;
    if (histKind  !== 'All' && subKind(s) !== histKind) return false;
    const baseDate = historyDate(s);
    if (histDateFrom && (!baseDate || baseDate < histDateFrom)) return false;
    if (histDateTo   && (!baseDate || baseDate > histDateTo))   return false;
    if (histOpenOnly && !openDatesSet.has(`${s.hub}|${s.txn_date}`)) return false;
    return true;
  }), [submissions, histHub, histMonth, histKind, histDateFrom, histDateTo, histOpenOnly, openDatesSet]); // eslint-disable-line

  const archivedHistHubs = useMemo(() => ['All', ...[...new Set(archivedSubmissions.map(s => s.hub).filter(Boolean))].sort()], [archivedSubmissions]);
  const archivedFilteredSubs = useMemo(() => archivedSubmissions.filter(s => {
    if (histHub   !== 'All' && s.hub !== histHub) return false;
    if (histMonth !== 'All' && subMonth(s) !== histMonth) return false;
    if (histKind  !== 'All' && subKind(s) !== histKind) return false;
    const baseDate = historyDate(s);
    if (histDateFrom && (!baseDate || baseDate < histDateFrom)) return false;
    if (histDateTo   && (!baseDate || baseDate > histDateTo))   return false;
    if (histOpenOnly && !openDatesSet.has(`${s.hub}|${s.txn_date}`)) return false;
    return true;
  }), [archivedSubmissions, histHub, histMonth, histKind, histDateFrom, histDateTo, histOpenOnly, openDatesSet]); // eslint-disable-line

  // All currently-flagged submissions (shown in the Flagged tab)
  const flaggedSubs = useMemo(() => submissions.filter(s => s.review_status === 'flagged'), [submissions]);

  function downloadHubSummaryExcel() {
    // Respect active filters — only include hubs currently visible in the summary table
    const filteredHubNames = new Set(filtered.map(h => h.hub));

    // Build one row per hub per date with CMS / NEFT-FKL / NEFT-WH totals
    const rows = [];
    const groupMap = {};
    transactions.forEach(t => {
      if (t.status === 'today_direct' || !t.collection_date) return;
      if (!filteredHubNames.has(t.hub)) return; // skip hubs excluded by search/zone filter
      if (summaryMonth !== 'All' && t.month !== summaryMonth) return; // respect month filter
      const key = `${t.hub}|${t.collection_date}`;
      if (!groupMap[key]) groupMap[key] = {
        date: t.collection_date, hub: t.hub, amount_to_collect: 0,
        cms: 0,          cms_refs: [],          cms_proofs: [],
        neft_fkl: 0,     neft_fkl_refs: [],     neft_fkl_proofs: [],
        neft_wh: 0,      neft_wh_refs: [],      neft_wh_proofs: [],
        hub_utilised: 0, hub_utilised_refs: [], hub_utilised_proofs: [],
      };
      groupMap[key].amount_to_collect += Number(t.amount_to_collect || 0);
      const subs = subByHubDate[key] || [];
      subs.forEach(s => {
        const amt    = Number(s.amount || 0);
        const ref    = s.ref_number || null;
        const core   = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
        const isLoss = core.startsWith('[LOSS]');
        if (isLoss) {
          groupMap[key].hub_utilised += amt;
          if (ref)       groupMap[key].hub_utilised_refs.push(ref);
          if (s.slip_url) groupMap[key].hub_utilised_proofs.push(s.slip_url);
        } else if (s.type === 'cms') {
          groupMap[key].cms += amt;
          if (ref)       groupMap[key].cms_refs.push(ref);
          if (s.slip_url) groupMap[key].cms_proofs.push(s.slip_url);
        } else if (s.type === 'neft_flipkart') {
          groupMap[key].neft_fkl += amt;
          if (ref)       groupMap[key].neft_fkl_refs.push(ref);
          if (s.slip_url) groupMap[key].neft_fkl_proofs.push(s.slip_url);
        } else if (s.type === 'neft_wheeley') {
          groupMap[key].neft_wh += amt;
          if (ref)       groupMap[key].neft_wh_refs.push(ref);
          if (s.slip_url) groupMap[key].neft_wh_proofs.push(s.slip_url);
        }
      });
    });
    Object.values(groupMap).forEach(r => rows.push(r));
    // Sort by hub then date
    rows.sort((a, b) => a.hub.localeCompare(b.hub) || a.date.localeCompare(b.date));
    const fname = `WHEELEY_HubDeposits_${new Date().toISOString().split('T')[0]}.xlsx`;
    exportHubSummaryToExcel(rows, fname);
  }

  function downloadHistoryExcel() {
    let fname = 'Wheeley_Submissions';
    if (histHub   !== 'All') fname += `_${histHub.replace(/\s+/g, '_')}`;
    if (histMonth !== 'All') fname += `_${histMonth}`;
    if (histDateFrom || histDateTo) fname += `_${histDateFrom || ''}to${histDateTo || ''}`;
    fname += `_${new Date().toISOString().split('T')[0]}.xlsx`;
    exportSubmissionsToExcel(filteredSubs.filter(s => s.review_status !== 'flagged'), fname);
  }

  function downloadArchivedHistoryExcel() {
    let fname = 'Wheeley_Archived_Submissions';
    if (histHub   !== 'All') fname += `_${histHub.replace(/\s+/g, '_')}`;
    if (histMonth !== 'All') fname += `_${histMonth}`;
    if (histDateFrom || histDateTo) fname += `_${histDateFrom || ''}to${histDateTo || ''}`;
    fname += `_${new Date().toISOString().split('T')[0]}.xlsx`;
    exportSubmissionsToExcel(archivedFilteredSubs, fname);
  }

  const inputStyle = {
    background: '#1a1f2e', color: '#f3f4f6',
    border: '1px solid #2a2f3e', borderRadius: 8,
    padding: '8px 14px', fontSize: 13,
    fontFamily: 'inherit',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b0e14', fontFamily: "'DM Mono', monospace", paddingBottom: selectedCells.size > 0 ? 48 : 0 }}>

      {/* Top bar */}
      <div style={{ background: '#0f1117', borderBottom: '1px solid #1e2433', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ background: '#f59e0b', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#0f1117', fontWeight: 900, fontSize: 16 }}>W</span>
            </div>
            <div>
              <div style={{ color: '#f59e0b', fontWeight: 800, fontSize: 14, letterSpacing: 3 }}>WHEELEY</div>
              <div style={{ color: '#4b5563', fontSize: 10 }}>ADMIN DASHBOARD</div>
            </div>
          </div>
          {/* ── Section switcher ── */}
          <div style={{ display: 'flex', background: '#0b0e14', border: '1px solid #1e2433', borderRadius: 10, padding: 3, gap: 2 }}>
            {[['payment', '💳 Payment Sheet'], ['cod', '📦 COD Reconciliation'], ['billing', '🧾 Billing'], ['users', '👥 Users']].map(([k, label]) => (
              <button key={k} onClick={() => { setSection(k); onSectionChange?.(k); }}
                style={{
                  padding: '7px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, transition: 'all .15s',
                  background: section === k ? (k === 'payment' ? '#1e3a5f' : k === 'billing' ? '#14532d' : '#1c1a00') : 'transparent',
                  color: section === k ? (k === 'payment' ? '#60a5fa' : k === 'billing' ? '#4ade80' : '#f59e0b') : '#4b5563',
                  boxShadow: section === k ? '0 1px 4px #00000060' : 'none',
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {section === 'cod' && (
            <>
              {uploadMeta ? (
                <div style={{ color: '#6b7280', fontSize: 11 }}>
                  Last upload: <strong style={{ color: '#9ca3af' }}>{uploadMeta.upload_date || uploadMeta.date}</strong>
                  &nbsp;·&nbsp;{uploadMeta.filename}
                </div>
              ) : (
                <div style={{ color: '#4b5563', fontSize: 11 }}>No upload on record — data from previous upload shown</div>
              )}
              <button onClick={onRefresh} style={{ ...inputStyle, cursor: 'pointer', padding: '8px 14px', fontSize: 11 }}>↻ Refresh</button>
              <button onClick={() => exportToExcel(filtered, `WHEELEY_Pendency_${new Date().toISOString().split('T')[0]}.xlsx`)}
                style={{ ...inputStyle, cursor: 'pointer', fontSize: 11 }}>
                ↓ Export
              </button>
              <button onClick={onNewUpload}
                style={{ background: '#f59e0b', color: '#0f1117', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: 'pointer' }}>
                + Upload File
              </button>
            </>
          )}
          {!fullAdmin && hubUser && (
            <>
              <span style={{ color: '#818cf8', fontSize: 11, fontWeight: 700 }}>🗼 {hubUser.username} · CONTROL TOWER</span>
              <button onClick={onLogout} title="Log out"
                style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#f87171', cursor: 'pointer', fontFamily: 'inherit' }}>
                🔓 Logout
              </button>
            </>
          )}
        </div>
      </div>

      {(section === 'cod' || section === 'users') && (<div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {section === 'cod' && (<>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { label: 'TOTAL TO COLLECT',    value: fmt(totalToCollect),      color: '#60a5fa', bg: '#0c1220',  sub: null },
            { label: 'TOTAL DEPOSITED',     value: fmt(totalDeposited),      color: '#a78bfa', bg: '#130c1c',  sub: null },
            { label: 'TOTAL DIFF (FILE)',   value: fmt(totalDiff),           color: '#fb923c', bg: '#1c1008',  sub: null },
            { label: 'SUBMITTED (LINKED)',  value: fmt(totalSubmitted),      color: '#34d399', bg: '#0c1c14',  sub: 'Against Excel transactions' },
            { label: 'TOTAL PENDENCY',      value: fmt(totalPendency),       color: '#f87171', bg: '#1c0f0f',  sub: 'Excl. future deposits' },
            { label: 'ALERT HUBS',          value: `${alertHubs.length} hubs`, color: '#f59e0b', bg: '#1c1400', sub: null },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, border: `1px solid ${k.color}30`, borderRadius: 12, padding: 20 }}>
              <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 2 }}>{k.label}</div>
              <div style={{ color: k.color, fontSize: 22, fontWeight: 900, marginTop: 8 }}>{k.value}</div>
              {k.sub && <div style={{ color: '#374151', fontSize: 10, marginTop: 4 }}>{k.sub}</div>}
            </div>
          ))}
        </div>

        {/* Future Date Deposits card — shown when any hub has untracked deposits */}

        {/* Alert Banner */}
        {alertHubs.length > 0 && (
          <div style={{ background: '#1c1000', border: '1px solid #f59e0b40', borderRadius: 12, padding: 16 }}>
            <SectionLabel>⚠ HUBS WITH NO SUBMISSION YET</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {alertHubs.map(h => (
                <span key={h.hub} style={{ background: '#f87171', color: '#fff', borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>
                  {h.hub} — {fmt(h.pendency)} pending
                </span>
              ))}
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div style={{ display: 'flex', gap: 8 }}>
          {[['summary', 'Hub Summary'], ['history', 'Submission History'], ['archived', 'Archived'], ['flagged', '⚑ Flagged'], ['reconciliation', 'Reconciliation'], ['uploads', 'Uploads']].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid',
                fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: 'pointer',
                borderColor: view === k
                  ? (k === 'flagged' ? '#f97316' : k === 'archived' ? '#60a5fa' : '#f59e0b')
                  : (k === 'flagged' && flaggedSubs.length > 0 ? '#7f1d1d' : k === 'archived' && archivedSubmissions.length > 0 ? '#1e3a5f' : '#2a2f3e'),
                background: view === k
                  ? (k === 'flagged' ? '#f97316' : k === 'archived' ? '#60a5fa' : '#f59e0b')
                  : (k === 'flagged' && flaggedSubs.length > 0 ? '#1a0808' : k === 'archived' && archivedSubmissions.length > 0 ? '#0c1a2e' : 'transparent'),
                color: view === k ? '#0f1117' : (k === 'flagged' && flaggedSubs.length > 0 ? '#f87171' : k === 'archived' && archivedSubmissions.length > 0 ? '#60a5fa' : '#6b7280'),
                transition: 'all .2s', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
              }}>
              {label}
              {k === 'flagged' && flaggedSubs.length > 0 && (
                <span style={{ background: view === k ? '#0f1117' : '#f87171', color: view === k ? '#f87171' : '#0f1117', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 900 }}>
                  {flaggedSubs.length}
                </span>
              )}
              {k === 'archived' && archivedSubmissions.length > 0 && (
                <span style={{ background: view === k ? '#0f1117' : '#60a5fa', color: view === k ? '#60a5fa' : '#0f1117', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 900 }}>
                  {archivedSubmissions.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {view === 'summary' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input placeholder="Search hub..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 200 }} />
              <select value={summaryMonth} onChange={e => setSummaryMonth(e.target.value)} style={{ ...inputStyle, fontWeight: summaryMonth !== 'All' ? 700 : 400, color: summaryMonth !== 'All' ? '#818cf8' : '#f3f4f6' }}>
                {summaryMonths.map(m => <option key={m}>{m}</option>)}
              </select>
              <select value={zoneFilter} onChange={e => setZoneFilter(e.target.value)} style={inputStyle}>
                {zones.map(z => <option key={z}>{z}</option>)}
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={inputStyle}>
                <option value="pendency">Sort: Pendency ↓</option>
                <option value="submitted">Sort: Submitted ↓</option>
                <option value="hub">Sort: Hub Name</option>
              </select>
              {summaryMonth !== 'All' && (
                <button onClick={() => setSummaryMonth('All')} style={{ background: '#1a1f2e', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>
                  ✕ Clear Month
                </button>
              )}
              <button
                onClick={() => { setTransactions([]); }}
                disabled={txnLoading}
                style={{ background: '#1a1f2e', color: txnLoading ? '#4b5563' : '#60a5fa', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: txnLoading ? 'default' : 'pointer', fontFamily: 'inherit' }}
                title="Reload transaction data from the database"
              >
                {txnLoading ? '…' : '↺ Refresh'}
              </button>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: '#4b5563', fontSize: 12 }}>
                  {filtered.length} hubs · {summaryMonth !== 'All' ? summaryMonth : 'All months'}
                </span>
                <button
                  onClick={downloadHubSummaryExcel}
                  disabled={transactions.length === 0}
                  style={{ background: transactions.length === 0 ? '#1a1f2e' : '#14532d', color: transactions.length === 0 ? '#4b5563' : '#34d399', border: `1px solid ${transactions.length === 0 ? '#2a2f3e' : '#34d399'}`, borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: transactions.length === 0 ? 'default' : 'pointer', fontFamily: 'inherit', letterSpacing: 1, whiteSpace: 'nowrap' }}
                  title="Download hub-wise date-wise deposit breakdown"
                >
                  ↓ DOWNLOAD EXCEL
                </button>
              </div>
            </div>

            {/* Table */}
            {loading ? <Spinner /> : (
              <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#1a1f2e' }}>
                      {['HUB', 'ZONE', 'HUB TYPE', 'TO COLLECT', 'DEPOSITED', 'DIFF', 'SUBMITTED', 'FUTURE DEP.', 'PENDENCY', 'STATUS', 'LAST SUBMISSION', ''].map(h => (
                        <th key={h} style={{ color: h === 'FUTURE DEP.' ? '#6366f1' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((h, i) => {
                      const toCollect  = h.total_to_collect || h.totalToCollect || 0;
                      const deposited  = h.total_deposited  || h.totalDeposited  || 0;
                      const diff       = h.total_diff       || h.totalDiff       || 0;
                      const pct        = toCollect > 0 ? Math.min(100, (h.submitted / toCollect) * 100) : 100;
                      const isExpanded = expandedHub === h.hub;
                      // Transactions for this hub (filtered by month if selected)
                      const hubTxns = transactions
                        .filter(t => t.hub === h.hub && t.status !== 'today_direct' && (summaryMonth === 'All' || t.month === summaryMonth))
                        .sort((a, b) => (a.collection_date || '').localeCompare(b.collection_date || ''));
                      return (
                        <>
                        <tr key={h.hub}
                          onClick={() => setExpandedHub(isExpanded ? null : h.hub)}
                          style={{ borderTop: '1px solid #1e2433', background: isExpanded ? '#111827' : i % 2 === 0 ? '#0f1117' : '#0d101a', cursor: 'pointer', transition: 'background .15s' }}>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ color: '#4b5563', fontSize: 10, transition: 'transform .2s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                              <div>
                                <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 600 }}>{h.hub}</div>
                                <div style={{ color: '#4b5563', fontSize: 10 }}>{h.subCount} submission{h.subCount !== 1 ? 's' : ''}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <Badge color={h.zone === 'North' ? 'blue' : 'orange'}>{h.zone}</Badge>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>{h.hub_type || h.hubType || '–'}</td>
                          <td style={{ padding: '12px 16px', color: '#60a5fa', fontSize: 13 }}>{fmt(toCollect)}</td>
                          <td style={{ padding: '12px 16px', color: '#a78bfa', fontSize: 13 }}>{fmt(deposited)}</td>
                          <td style={{ padding: '12px 16px', color: '#fb923c', fontSize: 13 }}>{fmt(diff)}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ color: '#34d399', fontSize: 13 }}>{fmt(h.submitted)}</div>
                            <div style={{ background: '#1a1f2e', borderRadius: 4, height: 4, width: 80, marginTop: 4 }}>
                              <div style={{ background: '#34d399', width: `${pct}%`, height: '100%', borderRadius: 4 }} />
                            </div>
                          </td>
                          {/* FUTURE DEP. — direct/untracked submissions, not counted in pendency */}
                          <td style={{ padding: '12px 16px' }}>
                            {h.futureDeposited > 0 ? (
                              <span style={{ color: '#818cf8', fontSize: 13, fontWeight: 700 }}>{fmt(h.futureDeposited)}</span>
                            ) : (
                              <span style={{ color: '#374151', fontSize: 12 }}>–</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ color: h.pendency > 0 ? '#f87171' : '#34d399', fontSize: 13, fontWeight: 700 }}>
                              {fmt(h.pendency)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {h.pendency === 0
                              ? <Badge color="green">CLEARED</Badge>
                              : h.subCount === 0
                                ? <Badge color="red">NO SUBMISSION</Badge>
                                : <Badge color="yellow">PARTIAL</Badge>}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>
                            {h.lastSub ? `${h.lastSub.type?.toUpperCase()} · ${h.lastSub.submission_date}` : '–'}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <button onClick={e => { e.stopPropagation(); openAdminSubmit(h.hub); }}
                              style={{ background: '#1e2a1e', color: '#34d399', border: '1px solid #34d39940', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              ✏️ Submit
                            </button>
                          </td>
                        </tr>

                        {/* ── Expanded date-wise breakdown ── */}
                        {isExpanded && (
                          <tr key={`${h.hub}-expand`} style={{ background: '#080b12' }}>
                            <td colSpan={12} style={{ padding: '0 0 12px 0', borderTop: '1px solid #1e2433' }}>
                              {txnLoading ? (
                                <div style={{ padding: '16px 32px', color: '#4b5563', fontSize: 12 }}>Loading transactions…</div>
                              ) : hubTxns.length === 0 ? (
                                <div style={{ padding: '16px 32px', color: '#4b5563', fontSize: 12 }}>No transaction rows found for this hub{summaryMonth !== 'All' ? ` in ${summaryMonth}` : ''}.</div>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: '#0f1117' }}>
                                      {['DATE', 'MONTH', 'TOTAL COLLECTION', 'SUBMITTED', 'SHORTFALL', 'EXCESS', 'STATUS', 'SUBMISSIONS'].map(col => (
                                        <th key={col} style={{ color: col === 'EXCESS' ? '#3b82f6' : '#374151', fontSize: 10, letterSpacing: 1, padding: '8px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      // Group same-date rows into one display row per date.
                                      // Skip dates where ALL rows have line_status Closed.
                                      const CLOSED_LS = ['CLOSED', 'Closed', 'closed'];
                                      const dateGroups = {};
                                      hubTxns.forEach(t => {
                                        const key = t.collection_date || '__no_date__';
                                        if (!dateGroups[key]) dateGroups[key] = [];
                                        dateGroups[key].push(t);
                                      });
                                      return Object.values(dateGroups)
                                        .filter(group => group.some(t => !CLOSED_LS.includes(t.line_status)))
                                        .sort((a, b) =>
                                          (a[0].collection_date || '').localeCompare(b[0].collection_date || ''));
                                    })().map(group => {
                                      const t      = group[0];
                                      const rowKey = group.map(tx => tx.id).join('-');
                                      const due    = group.reduce((s, tx) => s + Number(tx.amount_to_collect || 0), 0);
                                      // All submissions for this date (pooled across all transactions in the group)
                                      const allDateSubs = subByHubDate[`${t.hub}|${t.collection_date}`] || [];
                                      const orphanFallback = allDateSubs.length === 0 && due > 0
                                        ? (subHubOrphan[t.hub] || []).filter(s =>
                                            group.some(tx => Number(s.amount) === Number(tx.amount_to_collect)))
                                        : [];
                                      const txnSubs    = allDateSubs.length > 0 ? allDateSubs : orphanFallback;
                                      const directSubs = subByHubDateDirect[`${t.hub}|${t.collection_date}`] || [];
                                      const allSubs    = [...txnSubs, ...directSubs];
                                      const subTotal   = allSubs.reduce((acc, s) => acc + Number(s.amount), 0);
                                      const shortfall  = Math.max(0, due - subTotal);
                                      const excess     = Math.max(0, subTotal - due);
                                      const cleared    = shortfall === 0 && due > 0;
                                      const noSub      = allSubs.length === 0;
                                      return (
                                        <tr key={rowKey} style={{ borderTop: '1px solid #1e243380' }}>
                                          <td style={{ padding: '9px 16px', color: '#d1d5db', fontWeight: 600 }}>{t.collection_date}</td>
                                          <td style={{ padding: '9px 16px', color: '#6b7280' }}>{t.month || '–'}</td>
                                          {/* TOTAL COLLECTION — clickable */}
                                          {(() => {
                                            const ck = `exp-${rowKey}-due`;
                                            const isSel = selectedCells.has(ck);
                                            return (
                                              <td style={{ padding: '9px 16px', cursor: due > 0 ? 'cell' : 'default',
                                                  background: isSel ? '#1e3a1e' : 'transparent',
                                                  outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                                onClick={() => { if (due > 0) toggleCell(ck, due); }}
                                                title={due > 0 ? 'Click to add to sum' : undefined}>
                                                <span style={{ color: '#60a5fa', fontWeight: isSel ? 900 : 400 }}>{fmt(due)}</span>
                                              </td>
                                            );
                                          })()}
                                          {/* SUBMITTED — clickable */}
                                          {(() => {
                                            const ck = `exp-${rowKey}-sub`;
                                            const isSel = selectedCells.has(ck);
                                            return (
                                              <td style={{ padding: '9px 16px', cursor: subTotal > 0 ? 'cell' : 'default',
                                                  background: isSel ? '#1e3a1e' : 'transparent',
                                                  outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                                onClick={() => { if (subTotal > 0) toggleCell(ck, subTotal); }}
                                                title={subTotal > 0 ? 'Click to add to sum' : undefined}>
                                                {subTotal > 0
                                                  ? <span style={{ color: '#34d399', fontWeight: isSel ? 900 : 400 }}>{fmt(subTotal)}</span>
                                                  : <span style={{ color: '#374151' }}>–</span>}
                                              </td>
                                            );
                                          })()}
                                          {/* SHORTFALL — clickable */}
                                          {(() => {
                                            const ck = `exp-${rowKey}-short`;
                                            const isSel = selectedCells.has(ck);
                                            return (
                                              <td style={{ padding: '9px 16px', cursor: due > 0 ? 'cell' : 'default',
                                                  background: isSel ? '#1e3a1e' : 'transparent',
                                                  outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                                onClick={() => { if (due > 0) toggleCell(ck, shortfall); }}
                                                title={due > 0 ? 'Click to add to sum' : undefined}>
                                                {due === 0
                                                  ? <span style={{ color: '#374151' }}>–</span>
                                                  : <span style={{ color: shortfall > 0 ? '#f87171' : '#34d399', fontWeight: isSel ? 900 : 700 }}>{fmt(shortfall)}</span>}
                                              </td>
                                            );
                                          })()}
                                          {/* EXCESS — clickable */}
                                          {(() => {
                                            const ck = `exp-${rowKey}-excess`;
                                            const isSel = selectedCells.has(ck);
                                            return (
                                              <td style={{ padding: '9px 16px', cursor: excess > 0 ? 'cell' : 'default',
                                                  background: isSel ? '#0c1a2e' : 'transparent',
                                                  outline: isSel ? '2px solid #3b82f670' : 'none', outlineOffset: -2 }}
                                                onClick={() => { if (excess > 0) toggleCell(ck, excess); }}
                                                title={excess > 0 ? 'Click to add to sum' : undefined}>
                                                {excess > 0
                                                  ? <span style={{ color: '#3b82f6', fontWeight: isSel ? 900 : 700 }}>{fmt(excess)}</span>
                                                  : <span style={{ color: '#374151' }}>–</span>}
                                              </td>
                                            );
                                          })()}
                                          <td style={{ padding: '9px 16px' }}>
                                            {due === 0
                                              ? <span style={{ color: '#374151', fontSize: 10 }}>N/A</span>
                                              : cleared
                                                ? <span style={{ background: '#065f46', color: '#34d399', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>CLEARED</span>
                                                : noSub
                                                  ? <span style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PENDING</span>
                                                  : <span style={{ background: '#451a03', color: '#fb923c', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PARTIAL</span>}
                                          </td>
                                          <td style={{ padding: '9px 16px' }}>
                                            {allSubs.length === 0
                                              ? <span style={{ color: '#374151', fontSize: 11 }}>No submissions yet</span>
                                              : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                  {allSubs.map(s => {
                                                    const isLoss   = s.remarks?.startsWith('[LOSS]');
                                                    const isShort  = s.remarks?.startsWith('[SHORT]');
                                                    const isExcess = s.remarks?.startsWith('[EXCESS]');
                                                    const typeMap  = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                                                    const baseType = typeMap[s.type] || s.type?.toUpperCase() || '—';
                                                    const typeLabel = isLoss ? 'LOSS/UTILIZED' : isExcess ? 'EXCESS' : baseType;
                                                    const typeColor = isLoss ? '#f97316' : isExcess ? '#3b82f6' : '#60a5fa';
                                                    const cleanRemarks = s.remarks
                                                      ? s.remarks.replace(/^\[(LOSS|SHORT|EXCESS|ADMIN)\]\s*/, '').trim()
                                                      : '';
                                                    return (
                                                      <div key={s.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingBottom: 5, borderBottom: '1px solid #1e243350' }}>
                                                        {/* Info + action buttons row — single line, info truncates */}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                                                          <span title={[typeLabel, fmt(Number(s.amount)), s.submission_date, s.ref_number ? `#${s.ref_number}` : '', cleanRemarks].filter(Boolean).join(' · ')}
                                                            style={{ color: '#9ca3af', fontSize: 11, flex: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                            <span style={{ color: typeColor, fontWeight: 700 }}>{typeLabel}</span>
                                                            {isShort && <span style={{ color: '#f87171', fontWeight: 700 }}> · SHORT</span>}
                                                            {' · '}{fmt(Number(s.amount))}
                                                            {' · '}{s.submission_date}
                                                            {!isLoss && s.ref_number ? <span style={{ color: '#6b7280' }}> · #{s.ref_number}</span> : ''}
                                                            {cleanRemarks ? <span style={{ color: '#6b7280', fontStyle: 'italic' }}> · {cleanRemarks}</span> : ''}
                                                          </span>
                                                          {/* View Proof */}
                                                          {s.slip_url && (
                                                            <button onClick={() => setPreviewSlip(s.slip_url)}
                                                              style={{ color: '#f59e0b', fontSize: 10, fontWeight: 700, background: '#1c150080', border: '1px solid #f59e0b40', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                              📎 Proof
                                                            </button>
                                                          )}
                                                          {/* Send Back / Unflag */}
                                                          {s.review_status === 'flagged' ? (
                                                            <button onClick={() => handleClearFlag(s.id)}
                                                              style={{ background: '#1e3a2f', color: '#34d399', border: '1px solid #34d39930', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                              ↩ Unflag
                                                            </button>
                                                          ) : flagSubId !== s.id && (
                                                            <button onClick={() => { setFlagSubId(s.id); setFlagNote(''); }}
                                                              style={{ background: 'transparent', color: '#4b5563', border: '1px solid #1e2433', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                                                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#f97316'; }}
                                                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2433'; e.currentTarget.style.color = '#4b5563'; }}>
                                                              ⚑ Send Back
                                                            </button>
                                                          )}
                                                        </div>
                                                        {/* Review note when flagged */}
                                                        {s.review_status === 'flagged' && s.review_note && (
                                                          <div style={{ color: '#f87171', fontSize: 10, fontStyle: 'italic' }}>"{s.review_note}"</div>
                                                        )}
                                                        {/* Inline send-back form */}
                                                        {flagSubId === s.id && (
                                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            <input placeholder="Reason (optional)" value={flagNote}
                                                              onChange={e => setFlagNote(e.target.value)}
                                                              style={{ background: '#0b0e14', color: '#f3f4f6', border: '1px solid #f8717160', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 150 }} />
                                                            <div style={{ display: 'flex', gap: 5 }}>
                                                              <button onClick={() => handleFlagSubmission(s.id)} disabled={flagging}
                                                                style={{ background: '#7f1d1d', color: '#f87171', border: '1px solid #f8717140', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: flagging ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                                                                {flagging ? '...' : 'Send Back'}
                                                              </button>
                                                              <button onClick={() => { setFlagSubId(null); setFlagNote(''); }}
                                                                style={{ background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 7px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                                                ✕
                                                              </button>
                                                            </div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Hub Summary — Excel-style sum bar (shared selectedCells) */}

            {/* Admin Submit Modal */}
            {adminSubHub && (
              <div style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                onClick={e => { if (e.target === e.currentTarget) setAdminSubHub(null); }}>
                <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 20, width: '100%', maxWidth: 480, padding: 32, fontFamily: "'DM Mono', monospace" }}>
                  <div style={{ color: '#f59e0b', fontSize: 11, letterSpacing: 2, fontWeight: 700, marginBottom: 4 }}>ADMIN SUBMIT ON BEHALF OF</div>
                  <div style={{ color: '#f3f4f6', fontSize: 20, fontWeight: 900, marginBottom: 24 }}>{adminSubHub}</div>

                  <form onSubmit={handleAdminSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* Transaction selector */}
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>AGAINST TRANSACTION DATE</div>
                      <select value={adminSubTxnId} onChange={e => setAdminSubTxnId(e.target.value)}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}>
                        <option value="">— Untracked / Today's Date (Direct) —</option>
                        {transactions
                          .filter(t => t.hub === adminSubHub && t.status !== 'today_direct' && (t.diff || 0) > 0)
                          .sort((a, b) => (a.collection_date || '').localeCompare(b.collection_date || ''))
                          .map(t => (
                            <option key={t.id} value={t.id}>
                              {t.collection_date} · Due ₹{Number(t.diff).toLocaleString('en-IN')}
                            </option>
                          ))}
                      </select>
                      {txnLoading && <div style={{ color: '#4b5563', fontSize: 10, marginTop: 4 }}>Loading transactions…</div>}
                    </div>

                    {/* Payment type */}
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>PAYMENT MODE</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[['cms', '💵 CMS Handover'], ['neft_flipkart', '🏦 NEFT → Flipkart'], ['neft_wheeley', '🏦 NEFT → Wheeley']].map(([k, label]) => (
                          <button key={k} type="button" onClick={() => setAdminSubType(k)}
                            style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: '1px solid', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                              borderColor: adminSubType === k ? '#f59e0b' : '#2a2f3e',
                              background: adminSubType === k ? '#f59e0b20' : 'transparent',
                              color: adminSubType === k ? '#f59e0b' : '#6b7280' }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Amount + Date */}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>AMOUNT (₹)</div>
                        <input type="number" placeholder="0" value={adminSubAmount} onChange={e => setAdminSubAmount(e.target.value)}
                          required style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>DEPOSIT DATE</div>
                        <input type="date" value={adminSubDate} onChange={e => setAdminSubDate(e.target.value)}
                          required style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                      </div>
                    </div>

                    {/* Ref */}
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>REFERENCE NUMBER</div>
                      <input placeholder="UTR / Receipt No. (optional)" value={adminSubRef} onChange={e => setAdminSubRef(e.target.value)}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    </div>

                    {/* Remarks */}
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>REMARKS (OPTIONAL)</div>
                      <input placeholder="e.g. Submitted via admin panel" value={adminSubRemarks} onChange={e => setAdminSubRemarks(e.target.value)}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                      <button type="button" onClick={() => setAdminSubHub(null)}
                        style={{ flex: 1, padding: '12px', background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 10, fontFamily: 'inherit', fontSize: 12, cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button type="submit" disabled={adminSubLoading || !adminSubAmount}
                        style={{ flex: 2, padding: '12px', background: adminSubLoading ? '#374151' : '#f59e0b', color: '#0f1117', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 13, fontWeight: 900, cursor: 'pointer', letterSpacing: 1 }}>
                        {adminSubLoading ? 'Submitting…' : '✓ SUBMIT'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}

        {view === 'history' && (
          <>
            {/* Filters + Download bar */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Hub */}
              <select value={histHub} onChange={e => setHistHub(e.target.value)} style={inputStyle}>
                {histHubs.map(h => <option key={h}>{h}</option>)}
              </select>
              {/* Month */}
              <select value={histMonth} onChange={e => setHistMonth(e.target.value)} style={inputStyle}>
                {histMonths.map(m => <option key={m}>{m}</option>)}
              </select>
              {/* Kind */}
              <select value={histKind} onChange={e => setHistKind(e.target.value)} style={inputStyle}>
                {['All', 'Full', 'Short', 'Excess', 'Loss'].map(k => <option key={k}>{k}</option>)}
              </select>
              {/* Date from/to */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#4b5563', fontSize: 11 }}>From</span>
                <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)}
                  style={{ ...inputStyle, padding: '8px 10px', width: 138 }} />
                <span style={{ color: '#4b5563', fontSize: 11 }}>To</span>
                <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)}
                  style={{ ...inputStyle, padding: '8px 10px', width: 138 }} />
              </div>
              {/* Open dates toggle */}
              <button onClick={() => setHistOpenOnly(v => !v)}
                style={{ ...inputStyle, cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  background: histOpenOnly ? '#14532d' : '#1a1f2e',
                  color: histOpenOnly ? '#34d399' : '#f59e0b',
                  border: `1px solid ${histOpenOnly ? '#34d399' : '#f59e0b'}`,
                  whiteSpace: 'nowrap',
                }}>
                {histOpenOnly ? '✓ OPEN DATES' : '○ OPEN DATES'}
              </button>
              {/* Clear filters */}
              {(histHub !== 'All' || histMonth !== 'All' || histKind !== 'All' || histDateFrom || histDateTo || histOpenOnly) && (
                <button onClick={() => { setHistHub('All'); setHistMonth('All'); setHistKind('All'); setHistDateFrom(''); setHistDateTo(''); setHistOpenOnly(false); }}
                  style={{ ...inputStyle, cursor: 'pointer', fontSize: 11, color: '#f87171' }}>✕ Clear</button>
              )}
              <div style={{ marginLeft: 'auto', color: '#4b5563', fontSize: 12, alignSelf: 'center', whiteSpace: 'nowrap' }}>
                {filteredSubs.length} rows
              </div>
              {/* Download */}
              <button onClick={downloadHistoryExcel}
                style={{ background: '#0c2218', color: '#34d399', border: '1px solid #34d39940', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                ↓ Download Excel
              </button>
            </div>

            <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
              {loading ? <Spinner /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#1a1f2e' }}>
                      {['HUB', 'FOR DATE', 'TOTAL COLLECTION', 'KIND', 'PAYMENT', 'SUBMITTED', 'SHORT AMT', 'SUB DATE', 'REF / UTR', 'REMARKS', 'SLIP', 'SUBMITTED AT', 'SEND BACK', ''].map(h => (
                        <th key={h} style={{ color: h === 'SEND BACK' ? '#f97316' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubs.length === 0 ? (
                      <tr><td colSpan={14} style={{ color: '#4b5563', textAlign: 'center', padding: 40 }}>No submissions match the filters</td></tr>
                    ) : filteredSubs.map((s, i) => {
                      const kind = subKind(s);
                      // Clean remarks for display — strip [ADMIN] and kind prefixes
                      const rawRemarks = (s.remarks || '').replace(/^\[ADMIN\]\s*/i, '').trim();
                      const displayRemarks = rawRemarks
                        .replace(/^\[(LOSS|SHORT|EXCESS)\]\s*/i, '')
                        .trim() || null;
                      const kindColor = kind === 'Loss'   ? { bg: '#1c0e00', text: '#f97316' }
                        : kind === 'Short'  ? { bg: '#2d0f0f', text: '#f87171' }
                        : kind === 'Excess' ? { bg: '#0c1a2e', text: '#3b82f6' }
                        : { bg: '#0c1c14', text: '#34d399' };
                      return (
                        <tr key={s.id} style={{ borderTop: `1px solid ${s.review_status === 'flagged' ? '#7f1d1d' : '#1e2433'}`, background: s.review_status === 'flagged' ? '#1a0808' : (i % 2 === 0 ? '#0f1117' : '#0d101a') }}>
                          <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                            <div style={{ color: '#f3f4f6', fontSize: 13 }}>{s.hub}</div>
                            {s.review_status === 'flagged' && (
                              <div style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700, display: 'inline-block', marginTop: 3, letterSpacing: 1 }}>
                                ⚑ SENT BACK
                              </div>
                            )}
                          </td>
                          {/* FOR DATE — the collection date of the transaction */}
                          <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                            {s.txn_date
                              ? <span style={{ color: '#9ca3af', fontSize: 13 }}>{s.txn_date}</span>
                              : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                            {/* Only show UNTRACKED if date is NOT covered by current Excel upload */}
                            {s.is_direct && !txnByHubDate[`${s.hub}|${s.txn_date}`] && (
                              <div style={{ color: '#6366f1', fontSize: 9, fontWeight: 700, marginTop: 2 }}>UNTRACKED</div>
                            )}
                          </td>
                          {/* TOTAL COLLECTION — clickable for sum bar */}
                          {(() => {
                            const resolvedAmt = s.txn_amount_to_collect ??
                              (s.is_direct && s.txn_date ? txnByHubDate[`${s.hub}|${s.txn_date}`]?.amount_to_collect : null);
                            const ck = `hist-${s.id}-toCollect`;
                            const isSel = selectedCells.has(ck);
                            return (
                              <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', cursor: resolvedAmt != null ? 'cell' : 'default',
                                  background: isSel ? '#1e3a1e' : 'transparent',
                                  outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                onClick={() => { if (resolvedAmt != null) toggleCell(ck, resolvedAmt); }}
                                title={resolvedAmt != null ? 'Click to add to sum' : undefined}>
                                {resolvedAmt != null
                                  ? <span style={{ color: '#60a5fa', fontSize: 13, fontWeight: isSel ? 900 : 700 }}>{fmt(resolvedAmt)}</span>
                                  : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                              </td>
                            );
                          })()}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ background: kindColor.bg, color: kindColor.text, borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, display: 'inline-block' }}>
                              {kind === 'Loss' ? '💸 LOSS' : kind === 'Short' ? '⚠️ SHORT' : kind === 'Excess' ? '💰 EXCESS' : '✅ FULL'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {(() => {
                              const typeMap = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                              const label   = typeMap[s.type] || s.type?.toUpperCase() || '—';
                              if (kind === 'Loss')
                                return <span style={{ background: '#1c0e00', color: '#f97316', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' }}>LOSS/UTILIZED</span>;
                              const isNeft = s.type?.includes('neft');
                              return (
                                <span style={{
                                  background: isNeft ? '#0c1a2e' : '#1c1a00',
                                  color:      isNeft ? '#60a5fa' : '#fbbf24',
                                  borderRadius: 5, padding: '2px 8px',
                                  fontSize: 10, fontWeight: 700,
                                  display: 'inline-block', whiteSpace: 'nowrap',
                                  border: `1px solid ${isNeft ? '#1e3a5f' : '#3d3000'}`,
                                }}>
                                  {label}
                                </span>
                              );
                            })()}
                          </td>
                          {/* SUBMITTED — clickable */}
                          {(() => {
                            const ck = `hist-${s.id}-amount`;
                            const isSel = selectedCells.has(ck);
                            return (
                              <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', cursor: 'cell',
                                  background: isSel ? '#1e3a1e' : 'transparent',
                                  outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                onClick={() => toggleCell(ck, s.amount)}
                                title="Click to add to sum">
                                <span style={{ color: kind === 'Loss' ? '#f97316' : '#34d399', fontSize: 13, fontWeight: isSel ? 900 : 700 }}>
                                  {fmt(s.amount)}
                                </span>
                              </td>
                            );
                          })()}
                          {/* SHORT AMT — clickable */}
                          {(() => {
                            const resolvedAmt = s.txn_amount_to_collect ??
                              (s.is_direct && s.txn_date ? txnByHubDate[`${s.hub}|${s.txn_date}`]?.amount_to_collect : null);
                            const shortAmt = resolvedAmt != null ? Math.max(0, Number(resolvedAmt) - Number(s.amount)) : null;
                            const ck = `hist-${s.id}-short`;
                            const isSel = selectedCells.has(ck);
                            return (
                              <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', cursor: shortAmt != null ? 'cell' : 'default',
                                  background: isSel ? '#1e3a1e' : 'transparent',
                                  outline: isSel ? '2px solid #34d39970' : 'none', outlineOffset: -2 }}
                                onClick={() => { if (shortAmt != null) toggleCell(ck, shortAmt); }}
                                title={shortAmt != null ? 'Click to add to sum' : undefined}>
                                {shortAmt != null
                                  ? <span style={{ color: shortAmt > 0 ? '#f87171' : '#34d399', fontSize: 13, fontWeight: isSel ? 900 : 700 }}>
                                      {shortAmt > 0 ? fmt(shortAmt) : '₹0'}
                                    </span>
                                  : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                              </td>
                            );
                          })()}
                          <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13, whiteSpace: 'nowrap' }}>{s.submission_date}</td>
                          <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>{s.ref_number || '–'}</td>
                          <td style={{ padding: '12px 16px', maxWidth: 220 }}>
                            {displayRemarks
                              ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#6b7280', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                                    {displayRemarks}
                                  </span>
                                  {displayRemarks.length > 30 && (
                                    <button onClick={() => setViewRemarks(displayRemarks)}
                                      style={{ flexShrink: 0, background: '#1a1f2e', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                      ↗ Full
                                    </button>
                                  )}
                                </div>
                              : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {s.slip_url
                              ? <button onClick={() => setPreviewSlip(s.slip_url)} style={{ background: 'transparent', border: 'none', color: '#f59e0b', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>View</button>
                              : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#4b5563', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(s.created_at).toLocaleString('en-IN')}</td>
                          {/* SEND BACK FOR REVIEW */}
                          <td style={{ padding: '12px 16px', minWidth: 160 }}>
                            {s.review_status === 'flagged' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {s.review_note && (
                                  <div style={{ color: '#f87171', fontSize: 10, fontStyle: 'italic', maxWidth: 160, wordBreak: 'break-word' }}>
                                    "{s.review_note}"
                                  </div>
                                )}
                                <button onClick={() => handleClearFlag(s.id)}
                                  style={{ background: '#1e3a2f', color: '#34d399', border: '1px solid #34d39930', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                  ↩ Unflag
                                </button>
                              </div>
                            ) : flagSubId === s.id ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <input
                                  placeholder="Reason (optional)"
                                  value={flagNote}
                                  onChange={e => setFlagNote(e.target.value)}
                                  style={{ background: '#0b0e14', color: '#f3f4f6', border: '1px solid #f8717160', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 150 }}
                                />
                                <div style={{ display: 'flex', gap: 5 }}>
                                  <button onClick={() => handleFlagSubmission(s.id)} disabled={flagging}
                                    style={{ background: '#7f1d1d', color: '#f87171', border: '1px solid #f8717140', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: flagging ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                                    {flagging ? '...' : 'Send Back'}
                                  </button>
                                  <button onClick={() => { setFlagSubId(null); setFlagNote(''); }}
                                    style={{ background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 7px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    ✕
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => { setFlagSubId(s.id); setFlagNote(''); }}
                                style={{ background: 'transparent', color: '#4b5563', border: '1px solid #1e2433', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#f97316'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2433'; e.currentTarget.style.color = '#4b5563'; }}>
                                ⚑ Send Back
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {confirmDeleteSubId === s.id ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: '#f87171', fontSize: 11, whiteSpace: 'nowrap' }}>Archive?</span>
                                <button
                                  onClick={() => handleDeleteSubmission(s.id)}
                                  disabled={deletingSub}
                                  style={{
                                    background: '#3b0f0f', color: '#f87171',
                                    border: '1px solid #f8717140', borderRadius: 6,
                                    padding: '4px 10px', fontSize: 11, fontWeight: 700,
                                    cursor: deletingSub ? 'wait' : 'pointer', fontFamily: 'inherit',
                                  }}
                                >
                                  {deletingSub ? '...' : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteSubId(null)}
                                  style={{
                                    background: 'transparent', color: '#6b7280',
                                    border: '1px solid #2a2f3e', borderRadius: 6,
                                    padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                                    fontFamily: 'inherit',
                                  }}
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteSubId(s.id)}
                                style={{
                                  background: 'transparent', color: '#4b5563',
                                  border: '1px solid #1e2433', borderRadius: 6,
                                  padding: '4px 10px', fontSize: 11,
                                  cursor: 'pointer', fontFamily: 'inherit',
                                  transition: 'all .15s',
                                }}
                                onMouseEnter={e => { e.target.style.borderColor = '#f87171'; e.target.style.color = '#f87171'; }}
                                onMouseLeave={e => { e.target.style.borderColor = '#1e2433'; e.target.style.color = '#4b5563'; }}
                              >
                                🗑
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Submission History — Excel-style sum bar (shared selectedCells) */}
          </>
        )}

        {/* ── Flagged Submissions tab ─────────────────────────────────────── */}
        {view === 'archived' && (
          <>
            <div style={{ background: '#0c1a2e', border: '1px solid #60a5fa40', borderRadius: 12, padding: '12px 16px', color: '#93c5fd', fontSize: 12 }}>
              Archived submissions are preserved audit records. They do not affect current pendency, but remain available for history and verification.
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={histHub} onChange={e => setHistHub(e.target.value)} style={inputStyle}>
                {archivedHistHubs.map(h => <option key={h}>{h}</option>)}
              </select>
              <select value={histMonth} onChange={e => setHistMonth(e.target.value)} style={inputStyle}>
                {histMonths.map(m => <option key={m}>{m}</option>)}
              </select>
              <select value={histKind} onChange={e => setHistKind(e.target.value)} style={inputStyle}>
                {['All', 'Full', 'Short', 'Excess', 'Loss'].map(k => <option key={k}>{k}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#4b5563', fontSize: 11 }}>From</span>
                <input type="date" value={histDateFrom} onChange={e => setHistDateFrom(e.target.value)} style={{ ...inputStyle, padding: '8px 10px', width: 138 }} />
                <span style={{ color: '#4b5563', fontSize: 11 }}>To</span>
                <input type="date" value={histDateTo} onChange={e => setHistDateTo(e.target.value)} style={{ ...inputStyle, padding: '8px 10px', width: 138 }} />
              </div>
              {(histHub !== 'All' || histMonth !== 'All' || histKind !== 'All' || histDateFrom || histDateTo || histOpenOnly) && (
                <button onClick={() => { setHistHub('All'); setHistMonth('All'); setHistKind('All'); setHistDateFrom(''); setHistDateTo(''); setHistOpenOnly(false); }}
                  style={{ ...inputStyle, cursor: 'pointer', fontSize: 11, color: '#f87171' }}>✕ Clear</button>
              )}
              <div style={{ marginLeft: 'auto', color: '#4b5563', fontSize: 12, alignSelf: 'center', whiteSpace: 'nowrap' }}>
                {archivedFilteredSubs.length} archived rows
              </div>
              <button onClick={downloadArchivedHistoryExcel}
                style={{ background: '#0c2218', color: '#34d399', border: '1px solid #34d39940', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                ↓ Download Excel
              </button>
            </div>

            <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
              {loading ? <Spinner /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#1a1f2e' }}>
                      {['HUB', 'FOR DATE', 'TOTAL COLLECTION', 'KIND', 'PAYMENT', 'SUBMITTED', 'SUB DATE', 'REF / UTR', 'ARCHIVE REASON', 'ARCHIVED AT', 'REMARKS', 'SLIP'].map(h => (
                        <th key={h} style={{ color: h === 'ARCHIVE REASON' ? '#60a5fa' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {archivedFilteredSubs.length === 0 ? (
                      <tr><td colSpan={12} style={{ color: '#4b5563', textAlign: 'center', padding: 40 }}>No archived submissions match the filters</td></tr>
                    ) : archivedFilteredSubs.map((s, i) => {
                      const kind = subKind(s);
                      const rawRemarks = (s.remarks || '').replace(/^\[ADMIN\]\s*/i, '').trim();
                      const displayRemarks = rawRemarks.replace(/^\[(LOSS|SHORT|EXCESS)\]\s*/i, '').trim() || null;
                      const resolvedAmt = s.txn_amount_to_collect ?? (s.is_direct && s.txn_date ? txnByHubDate[`${s.hub}|${s.txn_date}`]?.amount_to_collect : null);
                      const typeMap = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                      return (
                        <tr key={s.id} style={{ borderTop: '1px solid #1e2433', background: i % 2 === 0 ? '#0f1117' : '#0d101a' }}>
                          <td style={{ padding: '12px 16px', color: '#f3f4f6', fontSize: 13, whiteSpace: 'nowrap' }}>{s.hub}</td>
                          <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13, whiteSpace: 'nowrap' }}>{s.txn_date || '–'}</td>
                          <td style={{ padding: '12px 16px', color: resolvedAmt != null ? '#60a5fa' : '#374151', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{resolvedAmt != null ? fmt(resolvedAmt) : '–'}</td>
                          <td style={{ padding: '12px 16px', color: kind === 'Loss' ? '#f97316' : kind === 'Short' ? '#f87171' : kind === 'Excess' ? '#3b82f6' : '#34d399', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{kind}</td>
                          <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>{kind === 'Loss' ? 'LOSS/UTILIZED' : (typeMap[s.type] || s.type?.toUpperCase() || '—')}</td>
                          <td style={{ padding: '12px 16px', color: kind === 'Loss' ? '#f97316' : '#34d399', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(s.amount)}</td>
                          <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13, whiteSpace: 'nowrap' }}>{s.submission_date}</td>
                          <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>{s.ref_number || '–'}</td>
                          <td style={{ padding: '12px 16px', color: '#60a5fa', fontSize: 11, whiteSpace: 'nowrap' }}>{s.archive_reason || 'archived'}</td>
                          <td style={{ padding: '12px 16px', color: '#4b5563', fontSize: 11, whiteSpace: 'nowrap' }}>{s.archived_at ? new Date(s.archived_at).toLocaleString('en-IN') : '–'}</td>
                          <td style={{ padding: '12px 16px', maxWidth: 220 }}>
                            {displayRemarks
                              ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#6b7280', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{displayRemarks}</span>
                                  {displayRemarks.length > 30 && (
                                    <button onClick={() => setViewRemarks(displayRemarks)}
                                      style={{ flexShrink: 0, background: '#1a1f2e', color: '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                                      ↗ Full
                                    </button>
                                  )}
                                </div>
                              : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {s.slip_url
                              ? <button onClick={() => setPreviewSlip(s.slip_url)} style={{ background: 'transparent', border: 'none', color: '#f59e0b', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>View</button>
                              : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {view === 'flagged' && (
          <>
            {flaggedSubs.length === 0 ? (
              <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                <div style={{ color: '#34d399', fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>NO FLAGGED SUBMISSIONS</div>
                <div style={{ color: '#374151', fontSize: 12, marginTop: 6 }}>All clear — no submissions have been sent back for correction.</div>
              </div>
            ) : (
              <div style={{ background: '#0f1117', border: '1px solid #7f1d1d', borderRadius: 12, overflow: 'auto' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #7f1d1d', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#f87171', fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>⚑ SENT BACK FOR CORRECTION</span>
                  <span style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 10, padding: '1px 9px', fontSize: 11, fontWeight: 900 }}>{flaggedSubs.length}</span>
                  <span style={{ color: '#4b5563', fontSize: 11, marginLeft: 4 }}>Hub team has been notified. Unflag once resubmission is verified.</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#1a0808' }}>
                      {['HUB', 'FOR DATE', 'KIND', 'PAYMENT', 'SUBMITTED', 'SUB DATE', 'REF / UTR', 'REVIEW NOTE', 'SLIP', 'ACTION'].map(h => (
                        <th key={h} style={{ color: h === 'ACTION' ? '#f97316' : '#6b7280', fontSize: 10, letterSpacing: 1, padding: '11px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flaggedSubs.map((s, i) => {
                      const kind = subKind(s);
                      const kindColor = kind === 'Loss'   ? { bg: '#1c0e00', text: '#f97316' }
                        : kind === 'Short'  ? { bg: '#2d0f0f', text: '#f87171' }
                        : kind === 'Excess' ? { bg: '#0c1a2e', text: '#3b82f6' }
                        : { bg: '#0c1c14', text: '#34d399' };
                      const typeMap = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                      const payLabel = kind === 'Loss' ? 'LOSS/UTILIZED' : (typeMap[s.type] || s.type?.toUpperCase() || '—');
                      const displayRemarks = s.remarks?.replace(/^\[(LOSS|SHORT|EXCESS|ADMIN)\]\s*/, '').trim() || '';
                      return (
                        <tr key={s.id} style={{ borderTop: '1px solid #7f1d1d40', background: i % 2 === 0 ? '#140808' : '#0f0606' }}>
                          <td style={{ padding: '11px 16px', color: '#f3f4f6', fontWeight: 700, whiteSpace: 'nowrap' }}>{s.hub}</td>
                          <td style={{ padding: '11px 16px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                            {s.txn_date || '—'}
                            {s.is_direct && <div style={{ color: '#6366f1', fontSize: 9, fontWeight: 700 }}>UNTRACKED</div>}
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <span style={{ background: kindColor.bg, color: kindColor.text, borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {kind === 'Loss' ? '💸 LOSS' : kind === 'Short' ? '⚠️ SHORT' : kind === 'Excess' ? '💰 EXCESS' : '✅ FULL'}
                            </span>
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <span style={{ background: '#1c1a00', color: '#fbbf24', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{payLabel}</span>
                          </td>
                          <td style={{ padding: '11px 16px', color: '#34d399', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(Number(s.amount))}</td>
                          <td style={{ padding: '11px 16px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{s.submission_date || '—'}</td>
                          <td style={{ padding: '11px 16px', color: '#6b7280' }}>{s.ref_number || '—'}</td>
                          <td style={{ padding: '11px 16px', maxWidth: 220 }}>
                            {s.review_note
                              ? <span style={{ color: '#f87171', fontSize: 12, fontStyle: 'italic' }}>"{s.review_note}"</span>
                              : <span style={{ color: '#374151', fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            {s.slip_url
                              ? <button onClick={() => setPreviewSlip(s.slip_url)}
                                  style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700, background: '#1c150080', border: '1px solid #f59e0b40', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                  📎 Proof
                                </button>
                              : <span style={{ color: '#374151', fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ padding: '11px 16px' }}>
                            <button onClick={() => handleClearFlag(s.id)}
                              style={{ background: '#0c2218', color: '#34d399', border: '1px solid #34d39940', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', letterSpacing: 1 }}>
                              ✓ Resolved — Unflag
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {view === 'uploads' && (
          <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
            {uploadsLoading ? <Spinner /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1a1f2e' }}>
                    {['#', 'FILENAME', 'UPLOAD DATE', 'UPLOADED AT', 'DOWNLOAD', 'DELETE FILE', 'DELETE DATA'].map(h => (
                      <th key={h} style={{ color: '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 20px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uploads.length === 0 ? (
                    <tr><td colSpan={7} style={{ color: '#4b5563', textAlign: 'center', padding: 40 }}>No uploads yet</td></tr>
                  ) : uploads.map((u, i) => (
                    <tr key={u.id} style={{ borderTop: '1px solid #1e2433', background: i % 2 === 0 ? '#0f1117' : '#0d101a' }}>
                      <td style={{ padding: '14px 20px', color: '#374151', fontSize: 12 }}>{u.id}</td>
                      <td style={{ padding: '14px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 18 }}>📊</span>
                          <div>
                            <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 600 }}>{u.filename}</div>
                            {i === 0 && (
                              <span style={{ background: '#f59e0b20', color: '#f59e0b', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: 1 }}>LATEST</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 20px', color: '#9ca3af', fontSize: 13 }}>{u.upload_date}</td>
                      <td style={{ padding: '14px 20px', color: '#6b7280', fontSize: 12 }}>{new Date(u.created_at).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '14px 20px' }}>
                        {u.file_url ? (
                          <a
                            href={u.file_url}
                            download={u.filename}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              background: '#1a2f1e', color: '#34d399',
                              border: '1px solid #34d39930', borderRadius: 6,
                              padding: '6px 14px', fontSize: 11, fontWeight: 700,
                              textDecoration: 'none', letterSpacing: 1,
                            }}
                          >
                            ↓ Download
                          </a>
                        ) : (
                          <span style={{ color: '#374151', fontSize: 12 }}>Not stored</span>
                        )}
                      </td>
                      {/* DELETE FILE — removes upload record + storage file + hub_data + transactions */}
                      <td style={{ padding: '14px 20px' }}>
                        {confirmDeleteId === u.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
                            <span style={{ color: '#f87171', fontSize: 11, lineHeight: 1.4 }}>
                              ⚠️ Deletes file, all transactions &amp; hub data. Submission history preserved.
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => handleDeleteUpload(u)}
                              disabled={deleting}
                              style={{
                                background: '#3b0f0f', color: '#f87171',
                                border: '1px solid #f8717140', borderRadius: 6,
                                padding: '5px 12px', fontSize: 11, fontWeight: 700,
                                cursor: deleting ? 'wait' : 'pointer', fontFamily: 'inherit',
                              }}
                            >
                              {deleting ? '...' : 'Yes'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              style={{
                                background: 'transparent', color: '#6b7280',
                                border: '1px solid #2a2f3e', borderRadius: 6,
                                padding: '5px 10px', fontSize: 11, cursor: 'pointer',
                                fontFamily: 'inherit',
                              }}
                            >
                              No
                            </button>
                          </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setConfirmDeleteId(u.id); setConfirmDeleteDataId(null); }}
                            style={{
                              background: 'transparent', color: '#6b7280',
                              border: '1px solid #2a2f3e', borderRadius: 6,
                              padding: '6px 14px', fontSize: 11, fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'inherit',
                              transition: 'all .15s',
                            }}
                            onMouseEnter={e => { e.target.style.borderColor = '#f87171'; e.target.style.color = '#f87171'; }}
                            onMouseLeave={e => { e.target.style.borderColor = '#2a2f3e'; e.target.style.color = '#6b7280'; }}
                          >
                            🗑 File
                          </button>
                        )}
                      </td>

                      {/* DELETE DATA — wipes hub_data + transactions; submissions are intentionally preserved */}
                      <td style={{ padding: '14px 20px' }}>
                        {confirmDeleteDataId === u.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
                            <div style={{ color: '#f87171', fontSize: 11, lineHeight: 1.4 }}>
                              ⚠️ This will permanently delete all transactions &amp; hub data for this upload. Submission history is preserved.
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => handleDeleteData(u)}
                                disabled={deletingData}
                                style={{
                                  background: '#3b0f0f', color: '#f87171',
                                  border: '1px solid #f8717160', borderRadius: 6,
                                  padding: '5px 12px', fontSize: 11, fontWeight: 700,
                                  cursor: deletingData ? 'wait' : 'pointer', fontFamily: 'inherit',
                                }}
                              >
                                {deletingData ? 'Deleting...' : 'Yes, Delete All Data'}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteDataId(null)}
                                style={{
                                  background: 'transparent', color: '#6b7280',
                                  border: '1px solid #2a2f3e', borderRadius: 6,
                                  padding: '5px 10px', fontSize: 11, cursor: 'pointer',
                                  fontFamily: 'inherit',
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setConfirmDeleteDataId(u.id); setConfirmDeleteId(null); }}
                            style={{
                              background: 'transparent', color: '#6b7280',
                              border: '1px solid #2a2f3e', borderRadius: 6,
                              padding: '6px 14px', fontSize: 11, fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'inherit',
                              transition: 'all .15s',
                            }}
                            onMouseEnter={e => { e.target.style.borderColor = '#fb923c'; e.target.style.color = '#fb923c'; }}
                            onMouseLeave={e => { e.target.style.borderColor = '#2a2f3e'; e.target.style.color = '#6b7280'; }}
                          >
                            🧹 Data
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {view === 'reconciliation' && (
          <>
            {/* Reconciliation filters */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                placeholder="Search transaction ID, partner..."
                value={recoSearch}
                onChange={e => { setRecoSearch(e.target.value); setRecoPage(1); }}
                style={{ ...inputStyle, width: 260 }}
              />
              <select value={recoHub} onChange={e => { setRecoHub(e.target.value); setRecoPage(1); }} style={inputStyle}>
                <option value="All">All Hubs</option>
                {[...new Set(transactions.map(t => t.hub))].sort().map(h => (
                  <option key={h}>{h}</option>
                ))}
              </select>
              <button onClick={fetchTransactions} style={{ ...inputStyle, cursor: 'pointer', fontSize: 11 }}>↻ Reload</button>
              {(() => {
                const totalReco = transactions.filter(t =>
                  (recoHub === 'All' || t.hub === recoHub) &&
                  (!recoSearch || [t.transaction_id, t.partner_name, t.hub].some(v => v && v.toLowerCase().includes(recoSearch.toLowerCase())))
                ).length;
                const totalPages = Math.max(1, Math.ceil(totalReco / RECO_PAGE_SIZE));
                return (
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#4b5563', fontSize: 12 }}>
                      {totalReco} rows · page {recoPage}/{totalPages}
                    </span>
                    <button onClick={() => setRecoPage(p => Math.max(1, p - 1))} disabled={recoPage === 1}
                      style={{ background: '#1a1f2e', color: recoPage === 1 ? '#374151' : '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: recoPage === 1 ? 'default' : 'pointer' }}>‹</button>
                    <button onClick={() => setRecoPage(p => Math.min(totalPages, p + 1))} disabled={recoPage === totalPages}
                      style={{ background: '#1a1f2e', color: recoPage === totalPages ? '#374151' : '#9ca3af', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: recoPage === totalPages ? 'default' : 'pointer' }}>›</button>
                  </div>
                );
              })()}
            </div>

            <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
              {txnLoading ? <Spinner /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#1a1f2e' }}>
                      {[
                        'HUB', 'DATE', 'PARTNER', 'TXN ID',
                        'TO COLLECT', 'COD CREDIT 1', 'COD CREDIT 2', 'DIFF',
                        'DN APPLIED', 'DN REF', 'DN DATE',
                        'BANK REF', 'REF-1', 'STATUS',
                      ].map(h => (
                        <th key={h} style={{ color: '#4b5563', fontSize: 10, letterSpacing: 1, padding: '10px 14px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions
                      .filter(t =>
                        (recoHub === 'All' || t.hub === recoHub) &&
                        (!recoSearch || [t.transaction_id, t.partner_name, t.hub].some(v => v && v.toLowerCase().includes(recoSearch.toLowerCase())))
                      )
                      .slice((recoPage - 1) * RECO_PAGE_SIZE, recoPage * RECO_PAGE_SIZE)
                      .map((t, i) => {
                        const isDnSettled = t.dn_applied === 0;
                        return (
                          <tr key={t.id} style={{ borderTop: '1px solid #1e2433', background: i % 2 === 0 ? '#0f1117' : '#0d101a' }}>
                            <td style={{ padding: '10px 14px', color: '#f3f4f6', fontWeight: 600, whiteSpace: 'nowrap' }}>{t.hub}</td>
                            <td style={{ padding: '10px 14px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{t.collection_date || '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#9ca3af', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.partner_name || '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#6b7280', fontFamily: 'monospace', fontSize: 11 }}>{t.transaction_id || '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#60a5fa', textAlign: 'right' }}>{fmt(t.amount_to_collect)}</td>
                            <td style={{ padding: '10px 14px', color: '#34d399', textAlign: 'right' }}>{t.cod_credit_1 ? fmt(t.cod_credit_1) : '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#34d399', textAlign: 'right' }}>{t.cod_credit_2 ? fmt(t.cod_credit_2) : '–'}</td>
                            <td style={{ padding: '10px 14px', color: t.diff > 0 ? '#f87171' : '#34d399', textAlign: 'right', fontWeight: 700 }}>{fmt(t.diff)}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                              {t.dn_applied != null
                                ? <Badge color={isDnSettled ? 'green' : 'yellow'}>{isDnSettled ? 'DN SETTLED' : fmt(t.dn_applied)}</Badge>
                                : <span style={{ color: '#374151' }}>–</span>}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#a78bfa', fontFamily: 'monospace', fontSize: 11 }}>{t.dn_ref || '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{t.dn_date || '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#6b7280', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.bank_ref_1 || '–'}</td>
                            <td style={{ padding: '10px 14px', color: '#6b7280', fontFamily: 'monospace', fontSize: 11 }}>{t.ref_1 || '–'}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <Badge color={t.line_status === 'Open' || t.line_status === 'OPEN' ? 'yellow' : 'blue'}>
                                {t.line_status || t.status || '–'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    {transactions.length === 0 && !txnLoading && (
                      <tr><td colSpan={14} style={{ color: '#4b5563', textAlign: 'center', padding: 40 }}>No transaction data. Upload an Excel file first.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        </>)}

        {section === 'users' && (
          <>
            {/* ── Create User Form ─────────────────────────────────────────── */}
            <div style={{
              background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12,
              padding: '24px 28px',
            }}>
              <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 11, letterSpacing: 2, marginBottom: 16 }}>
                ➕ CREATE NEW USER
              </div>
              <form onSubmit={handleCreateUser} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>USERNAME</label>
                  <input value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="e.g. hub_delhi" style={{ ...inputStyle, width: 180 }} required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>PASSWORD</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Password" style={{ ...inputStyle, width: 160 }} required />
                </div>
                {/* Role selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>ROLE</label>
                  <select value={newUserRole} onChange={e => { setNewUserRole(e.target.value); setNewHubName(''); setNewManagedHubs(new Set()); }} style={{ ...inputStyle, width: 150 }}>
                    <option value="hub">Hub User</option>
                    <option value="manager">Manager</option>
                    {fullAdmin && <option value="control_tower">Control Tower</option>}
                  </select>
                </div>
                {/* Hub User: single hub dropdown */}
                {newUserRole === 'hub' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>ASSIGN HUB</label>
                    <select value={newHubName} onChange={e => setNewHubName(e.target.value)} style={{ ...inputStyle, width: 200 }} required>
                      <option value="">— Select Hub —</option>
                      {[...new Set(hubData.map(h => h.hub))].sort().map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Manager: multi-select list of hubs */}
                {newUserRole === 'manager' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, fontWeight: 700 }}>
                      MANAGED HUBS <span style={{ color: '#818cf8' }}>({newManagedHubs.size} selected)</span>
                    </label>
                    <div style={{ ...inputStyle, width: 260, maxHeight: 140, overflowY: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {[...new Set(hubData.map(h => h.hub))].sort().map(hubName => (
                        <label key={hubName} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: newManagedHubs.has(hubName) ? '#f3f4f6' : '#6b7280', fontSize: 12 }}>
                          <input type="checkbox" checked={newManagedHubs.has(hubName)}
                            onChange={e => {
                              const next = new Set(newManagedHubs);
                              if (e.target.checked) next.add(hubName); else next.delete(hubName);
                              setNewManagedHubs(next);
                            }}
                            style={{ accentColor: '#818cf8', cursor: 'pointer' }}
                          />
                          {hubName}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <button type="submit" disabled={creatingUser}
                  style={{ background: '#f59e0b', color: '#0f1117', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 900, fontSize: 11, letterSpacing: 2, cursor: creatingUser ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                  {creatingUser ? '...' : 'CREATE'}
                </button>
              </form>
            </div>

            {/* ── Bulk Import ──────────────────────────────────────────────── */}
            <div style={{
              background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12,
              padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 14,
            }}>
              <div style={{ color: '#60a5fa', fontWeight: 700, fontSize: 11, letterSpacing: 2 }}>
                📥 BULK IMPORT FROM EXCEL
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Template download */}
                <button
                  onClick={downloadUsersTemplate}
                  style={{
                    background: '#0c1220', color: '#60a5fa',
                    border: '1px solid #60a5fa40', borderRadius: 8,
                    padding: '9px 18px', fontSize: 11, fontWeight: 700,
                    letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  ↓ Download Template
                </button>
                {/* Upload button — wraps a hidden file input */}
                <label style={{
                  background: importing ? '#1a2a1a' : '#0c1c14',
                  color: importing ? '#4b5563' : '#34d399',
                  border: '1px solid #34d39940', borderRadius: 8,
                  padding: '9px 18px', fontSize: 11, fontWeight: 700,
                  letterSpacing: 1, cursor: importing ? 'wait' : 'pointer', fontFamily: 'inherit',
                  display: 'inline-block',
                }}>
                  {importing ? '⏳ Importing...' : '↑ Upload & Import'}
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleBulkImport}
                    disabled={importing}
                    style={{ display: 'none' }}
                  />
                </label>
                <span style={{ color: '#374151', fontSize: 11 }}>
                  Columns required: <code style={{ color: '#6b7280' }}>username, password, hub_name</code>
                </span>
              </div>

              {/* Import results panel */}
              {importResults && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ background: '#0c1c14', color: '#34d399', borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                      ✅ Created: {importResults.created}
                    </span>
                    {importResults.skipped.length > 0 && (
                      <span style={{ background: '#1c1008', color: '#fb923c', borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                        ⚠️ Skipped: {importResults.skipped.length}
                      </span>
                    )}
                    {importResults.errors.length > 0 && (
                      <span style={{ background: '#1c0f0f', color: '#f87171', borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                        ❌ Parse errors: {importResults.errors.length}
                      </span>
                    )}
                  </div>
                  {/* Detail list of skipped / errors */}
                  {[...importResults.skipped, ...importResults.errors].length > 0 && (
                    <div style={{ background: '#0b0e14', border: '1px solid #1e2433', borderRadius: 8, padding: '10px 14px', maxHeight: 120, overflowY: 'auto' }}>
                      {[...importResults.skipped, ...importResults.errors].map((msg, i) => (
                        <div key={i} style={{ color: '#f87171', fontSize: 11, marginBottom: 3 }}>• {msg}</div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setImportResults(null)}
                    style={{ alignSelf: 'flex-start', background: 'transparent', color: '#4b5563', border: 'none', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>

            {/* ── Users Table ──────────────────────────────────────────────── */}
            <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
              {usersLoading ? <Spinner /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#1a1f2e' }}>
                      {['USERNAME', 'ROLE', 'HUB / MANAGED HUBS', 'STATUS', 'CREATED', 'RESET PASSWORD', 'EDIT ROLE / HUB', 'TOGGLE', 'DELETE'].map(h => (
                        <th key={h} style={{ color: h === 'EDIT ROLE / HUB' ? '#fbbf24' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 20px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hubUsers.length === 0 ? (
                      <tr><td colSpan={9} style={{ color: '#4b5563', textAlign: 'center', padding: 40 }}>No users yet — create one above.</td></tr>
                    ) : hubUsers.map((u, i) => (
                      <tr key={u.id} style={{ borderTop: '1px solid #1e2433', background: i % 2 === 0 ? '#0f1117' : '#0d101a' }}>
                        {/* USERNAME */}
                        <td style={{ padding: '14px 20px', color: '#f3f4f6', fontWeight: 700, fontSize: 13 }}>
                          {u.role === 'control_tower' ? '🗼' : u.role === 'manager' ? '🏢' : '👤'} {u.username}
                        </td>
                        {/* ROLE */}
                        <td style={{ padding: '14px 20px' }}>
                          {editUserId === u.id ? (
                            <select value={editRole}
                              onChange={e => { setEditRole(e.target.value); setEditHubName(''); setEditManagedHubs(new Set()); }}
                              style={{ background: '#1a1f2e', color: '#f3f4f6', border: '1px solid #2a2f3e', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontFamily: 'inherit', outline: 'none' }}>
                              <option value="hub">Hub User</option>
                              <option value="manager">Manager</option>
                              {fullAdmin && <option value="control_tower">Control Tower</option>}
                            </select>
                          ) : (
                            u.role === 'control_tower'
                              ? <span style={{ background: '#1c1a00', color: '#f59e0b', borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 700, border: '1px solid #f59e0b30' }}>CONTROL TOWER</span>
                              : u.role === 'manager'
                              ? <span style={{ background: '#1a1040', color: '#818cf8', borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 700, border: '1px solid #818cf830' }}>MANAGER</span>
                              : <span style={{ background: '#0c1a2e', color: '#60a5fa', borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>HUB USER</span>
                          )}
                        </td>
                        {/* HUB / MANAGED HUBS */}
                        <td style={{ padding: '14px 20px', maxWidth: 300 }}>
                          {editUserId === u.id ? (
                            editRole === 'hub' ? (
                              <select value={editHubName} onChange={e => setEditHubName(e.target.value)}
                                style={{ background: '#1a1f2e', color: '#60a5fa', border: '1px solid #2a2f3e', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontFamily: 'inherit', outline: 'none', minWidth: 200 }}>
                                <option value="">— Select Hub —</option>
                                {[...new Set(hubData.map(h => h.hub))].sort().map(h => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                            ) : (
                              <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 6, padding: '6px 10px', maxHeight: 150, overflowY: 'auto', minWidth: 220, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div style={{ color: '#818cf8', fontSize: 10, letterSpacing: 1, fontWeight: 700, marginBottom: 2 }}>
                                  MANAGED HUBS <span style={{ color: '#4b5563' }}>({editManagedHubs.size} selected)</span>
                                </div>
                                {[...new Set(hubData.map(h => h.hub))].sort().map(hubName => (
                                  <label key={hubName} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', color: editManagedHubs.has(hubName) ? '#f3f4f6' : '#6b7280', fontSize: 11 }}>
                                    <input type="checkbox" checked={editManagedHubs.has(hubName)}
                                      onChange={e => {
                                        const next = new Set(editManagedHubs);
                                        if (e.target.checked) next.add(hubName); else next.delete(hubName);
                                        setEditManagedHubs(next);
                                      }}
                                      style={{ accentColor: '#818cf8', cursor: 'pointer' }} />
                                    {hubName}
                                  </label>
                                ))}
                              </div>
                            )
                          ) : (
                            u.role === 'manager' ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {(u.managed_hubs || []).length === 0
                                  ? <span style={{ color: '#4b5563', fontSize: 11 }}>No hubs assigned</span>
                                  : (u.managed_hubs || []).map(h => (
                                      <span key={h} style={{ background: '#1a1040', color: '#a78bfa', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>{h}</span>
                                    ))}
                              </div>
                            ) : (
                              <span style={{ background: '#1e2433', color: '#60a5fa', borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>{u.hub_name}</span>
                            )
                          )}
                        </td>
                        {/* STATUS */}
                        <td style={{ padding: '14px 20px' }}>
                          <Badge color={u.is_active ? 'green' : 'red'}>
                            {u.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </Badge>
                        </td>
                        {/* CREATED */}
                        <td style={{ padding: '14px 20px', color: '#6b7280', fontSize: 12 }}>
                          {new Date(u.created_at).toLocaleDateString('en-IN')}
                        </td>
                        {/* RESET PASSWORD */}
                        <td style={{ padding: '14px 20px' }}>
                          {(!fullAdmin && u.role === 'control_tower') ? (<span style={{ color: '#374151', fontSize: 11 }}>🔒 Admin only</span>) : resetUserId === u.id ? (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                type="password"
                                placeholder="New password"
                                value={resetPassword}
                                onChange={e => setResetPassword(e.target.value)}
                                style={{ ...inputStyle, width: 140, fontSize: 11 }}
                              />
                              <button
                                onClick={() => handleResetPassword(u.id)}
                                disabled={resettingPw || !resetPassword}
                                style={{
                                  background: '#1e3a2f', color: '#34d399',
                                  border: '1px solid #34d39930', borderRadius: 6,
                                  padding: '5px 12px', fontSize: 11, fontWeight: 700,
                                  cursor: resettingPw ? 'wait' : 'pointer', fontFamily: 'inherit',
                                }}
                              >
                                {resettingPw ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={() => { setResetUserId(null); setResetPassword(''); }}
                                style={{
                                  background: 'transparent', color: '#6b7280',
                                  border: '1px solid #2a2f3e', borderRadius: 6,
                                  padding: '5px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setResetUserId(u.id); setResetPassword(''); }}
                              style={{
                                background: 'transparent', color: '#6b7280',
                                border: '1px solid #2a2f3e', borderRadius: 6,
                                padding: '6px 14px', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
                              }}
                              onMouseEnter={e => { e.target.style.borderColor = '#a78bfa'; e.target.style.color = '#a78bfa'; }}
                              onMouseLeave={e => { e.target.style.borderColor = '#2a2f3e'; e.target.style.color = '#6b7280'; }}
                            >
                              🔑 Reset
                            </button>
                          )}
                        </td>
                        {/* EDIT ROLE / HUB */}
                        <td style={{ padding: '14px 20px' }}>
                          {(!fullAdmin && u.role === 'control_tower') ? (<span style={{ color: '#374151', fontSize: 11 }}>🔒 Admin only</span>) : editUserId === u.id ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                onClick={() => handleSaveUserEdit(u.id)}
                                disabled={savingEdit}
                                style={{ background: '#1e3a2f', color: '#34d399', border: '1px solid #34d39930', borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: savingEdit ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                                {savingEdit ? '...' : '💾 Save'}
                              </button>
                              <button
                                onClick={() => setEditUserId(null)}
                                style={{ background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 6, padding: '5px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditUser(u)}
                              style={{ background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#fbbf24'; e.currentTarget.style.color = '#fbbf24'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2f3e'; e.currentTarget.style.color = '#6b7280'; }}>
                              ✏️ Edit
                            </button>
                          )}
                        </td>
                        {/* TOGGLE ACTIVE */}
                        <td style={{ padding: '14px 20px' }}>
                          {(!fullAdmin && u.role === 'control_tower') ? (<span style={{ color: '#374151', fontSize: 11 }}>🔒 Admin only</span>) : (
                          <button
                            onClick={() => handleToggleUserActive(u)}
                            style={{
                              background: u.is_active ? '#3b1515' : '#1e3a2f',
                              color: u.is_active ? '#f87171' : '#34d399',
                              border: `1px solid ${u.is_active ? '#f8717130' : '#34d39930'}`,
                              borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            {u.is_active ? '⏸ Deactivate' : '▶ Activate'}
                          </button>
                          )}
                        </td>
                        {/* DELETE */}
                        <td style={{ padding: '14px 20px' }}>
                          {(!fullAdmin && u.role === 'control_tower') ? (<span style={{ color: '#374151', fontSize: 11 }}>🔒 Admin only</span>) : confirmDeleteUserId === u.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <span style={{ color: '#f87171', fontSize: 11 }}>Delete {u.username}?</span>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  disabled={deletingUser}
                                  style={{
                                    background: '#3b0f0f', color: '#f87171',
                                    border: '1px solid #f8717140', borderRadius: 6,
                                    padding: '5px 12px', fontSize: 11, fontWeight: 700,
                                    cursor: deletingUser ? 'wait' : 'pointer', fontFamily: 'inherit',
                                  }}
                                >
                                  {deletingUser ? '...' : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteUserId(null)}
                                  style={{
                                    background: 'transparent', color: '#6b7280',
                                    border: '1px solid #2a2f3e', borderRadius: 6,
                                    padding: '5px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                                  }}
                                >
                                  No
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteUserId(u.id)}
                              style={{
                                background: 'transparent', color: '#6b7280',
                                border: '1px solid #2a2f3e', borderRadius: 6,
                                padding: '6px 14px', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
                              }}
                              onMouseEnter={e => { e.target.style.borderColor = '#f87171'; e.target.style.color = '#f87171'; }}
                              onMouseLeave={e => { e.target.style.borderColor = '#2a2f3e'; e.target.style.color = '#6b7280'; }}
                            >
                              🗑 Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

      </div>)}

      {/* ── Payment Sheet section ── */}
      {section === 'payment' && <PaymentSheet isAdmin={true} user={null} fullAdmin={fullAdmin} />}

      {/* ── Billing section ── */}
      {section === 'billing' && <BillingSection />}

      {/* ── Slip Preview Modal ──────────────────────────────────────────────── */}
      <SlipPreviewModal url={previewSlip} onClose={() => setPreviewSlip(null)} />

      {/* ── Full Remarks Modal ─────────────────────────────────────────────── */}
      {viewRemarks && (
        <div
          onClick={() => setViewRemarks(null)}
          style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 16, width: '100%', maxWidth: 480, padding: 28, fontFamily: "'DM Mono', monospace" }}>
            <div style={{ color: '#f59e0b', fontSize: 10, letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>FULL REMARKS</div>
            <div style={{ color: '#d1d5db', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {viewRemarks}
            </div>
            <button
              onClick={() => setViewRemarks(null)}
              style={{ marginTop: 20, width: '100%', padding: '10px', background: 'transparent', color: '#6b7280', border: '1px solid #2a2f3e', borderRadius: 8, fontFamily: 'inherit', fontSize: 12, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Global cell-selection sum bar (Excel-style) ── */}
      {selectedCells.size > 0 && (() => {
        const values = [...selectedCells.values()];
        const total  = values.reduce((a, v) => a + v, 0);
        const avg    = total / values.length;
        return (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 4000,
            background: '#0d1117', borderTop: '1px solid #2a2f3e',
            display: 'flex', alignItems: 'center', gap: 0,
            fontFamily: "'DM Mono', monospace", padding: '0 20px', height: 40,
          }}>
            <span style={{ color: '#4b5563', fontSize: 10, letterSpacing: 1, marginRight: 20 }}>
              {selectedCells.size} cell{selectedCells.size > 1 ? 's' : ''} selected
            </span>
            <span style={{ color: '#6b7280', fontSize: 11, marginRight: 24 }}>
              Average: <span style={{ color: '#9ca3af', fontWeight: 700 }}>{fmt(Math.round(avg))}</span>
            </span>
            <span style={{ color: '#6b7280', fontSize: 11, marginRight: 24 }}>
              Count: <span style={{ color: '#9ca3af', fontWeight: 700 }}>{values.length}</span>
            </span>
            <span style={{ color: '#6b7280', fontSize: 11, marginRight: 24 }}>
              Sum: <span style={{ color: '#f59e0b', fontSize: 14, fontWeight: 900 }}>{fmt(total)}</span>
            </span>
            <button onClick={() => setSelectedCells(new Map())}
              style={{ marginLeft: 'auto', background: 'transparent', color: '#4b5563', border: '1px solid #2a2f3e', borderRadius: 6, padding: '4px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
              ✕ Clear selection
            </button>
          </div>
        );
      })()}

    </div>
  );
}
